/* ============================================================
   ILS — Delivery selector — TASK-3135 (design) → TASK-3163 (live API)

   The visual prototype (country → method → operator → point, progressive
   disclosure, autoscroll, collapsible point list, two-line rows, minimal
   header, sticky Next/Back — TASK-3135…3151) is UNCHANGED. What changed in
   TASK-3163: the data no longer comes from hardcoded MOCK arrays — it is
   fetched live from the ils-admin delivery API:

     GET /api/delivery/config              — full country × method matrix
     GET /api/delivery/rates?country=XX    — real customer prices per method
     GET /api/delivery/points?country=XX&carrier=YY&search=&limit=&offset=
                                           — real locker points (trigram search)

   (Backed by TASK-3152/3153/3161/3162 on ils-admin; CORS for ilovesnacks.eu
    configured in TASK-3153. Carrier display names — incl. the LT "LP Express"
    brand for Unisend — arrive already resolved from the API.)

   Points strategy (see report): one 500-row page is fetched per operator on
   select (covers every current operator in a single request), the search box
   drives DEBOUNCED server-side trigram search through the same endpoint (so a
   term matches across the FULL set, not just the loaded page), and the fixed
   scroll window lazy-loads further pages by `offset` on scroll — never pulling
   the whole (potentially 2000+) point list to the client at once.

   All display copy still flows through the shared translator (data-i18n-* /
   `| t`); country names via data-country-names; operator brand names + point
   addresses are live API data, not translatable copy.

   TASK-3355 (speed-up): the cart persistence (attributes TASK-3157 + line-item
   properties TASK-3228) is no longer a single serial block run on the "Next"
   click. It is now pre-written to the cart in the BACKGROUND the moment the
   selection is complete (and re-written on every change), so Next → checkout is
   near-instant. The write channels/keys/format are byte-identical to before;
   only WHEN they run changed. See the "INCREMENTAL / PRE-EMPTIVE CART WRITE"
   block below for the serialised, signature-guarded queue.

   TASK-4850 (safe fast path): REPORT-4848 measured the click -> /checkout gap on
   live Production at 1 211 / 1 879 / 3 138 ms for 1 / 3 / 5 cart lines in the case
   shoppers complain about — country + courier are PRESELECTED, they tap nothing,
   so TASK-3355's `interacted` gate means no pre-write ever ran and the WHOLE
   persistence executes on the click path behind the gift gate. This task keeps
   every write channel, key and format identical and changes only scheduling and
   proof: a complete auto-preselected selection now persists in the background
   without needing a tap, readiness is keyed to the cart LINE signature as well as
   the selection signature (closing REPORT-4848 §5/§7 — a line added after the
   selection was persisted reached checkout with 0/6 _delivery_* properties), our
   own attribute/property writes are marked so custom-gift-logic.js stops waking
   its reconciler for them, and a ready Next performs ZERO cart API requests.
   All of it sits behind ONE kill switch — see ILS_FASTPATH_DEFAULT below.
   ============================================================ */
(function () {
  'use strict';

  var root = document.querySelector('[data-ils-dsel]');
  if (!root) return;

  /* ---- TASK-3163: live delivery API host (ils-admin dev/staging) ---------- */
  var API_BASE = 'https://alert-dedication-production.up.railway.app';

  /* ---- i18n from the section root (translated server-side) ---- */
  var I18N = {
    selected: root.getAttribute('data-i18n-selected') || 'Selected',
    noPoints: root.getAttribute('data-i18n-nopoints') || 'No locations match your search.',
    searchCountry: root.getAttribute('data-i18n-search-country') || 'Search countries',
    loading: root.getAttribute('data-i18n-loading') || 'Loading…',
    error: root.getAttribute('data-i18n-error') || 'Couldn’t load delivery options.',
    retry: root.getAttribute('data-i18n-retry') || 'Try again'
  };
  var COUNTRY_NAMES = {};
  try { COUNTRY_NAMES = JSON.parse(root.getAttribute('data-country-names') || '{}'); }
  catch (e) { COUNTRY_NAMES = {}; }

  // TASK-4833: the logged-in customer's default-address country, emitted by the
  // section from the storefront `customer` object (Liquid, already in the page —
  // no extra request, no new service). Empty string when nobody is logged in or
  // the account carries no address. Feeds step (b) of the preselection ladder.
  var CUSTOMER_COUNTRY = (root.getAttribute('data-customer-country') || '').trim().toLowerCase();

  // TASK-5769: the APPLIED Shopify Market country for this request, server-
  // rendered by the section (localization.country.iso_code) — the same value the
  // header, announcement strip and footer display. Owner directive 2026-10-24:
  // the country preselected on this step when the shopper arrives from the cart
  // is THIS, not geolocation, not an IP guess, not the storefront locale, not
  // Shopify's primary market (LV), and not whatever this calculator happened to
  // compute on an earlier page view. window.Shopify.country is only a fallback
  // for the (impossible in practice) case of the attribute being empty.
  // Sanitised to [a-z] here and matched against the 27 served countries in
  // resolveDefaultCountry(), so an unexpected value can never be selected.
  var SHOP_COUNTRY = (root.getAttribute('data-shop-country') || '').trim().toLowerCase().replace(/[^a-z]/g, '');
  if (!SHOP_COUNTRY) {
    try { SHOP_COUNTRY = String((window.Shopify && window.Shopify.country) || '').trim().toLowerCase().replace(/[^a-z]/g, ''); } catch (e) { SHOP_COUNTRY = ''; }
  }

  // TASK-3140: operator id → logo asset URL, emitted by Liquid (asset_url).
  // An operator missing from this map falls back to a coloured letter avatar.
  var OP_LOGOS = {};
  try { OP_LOGOS = JSON.parse(root.getAttribute('data-op-logos') || '{}'); }
  catch (e) { OP_LOGOS = {}; }

  /* ---- PRESENTATION CONSTANTS (frontend-only, not data) ------------------- */
  // TASK-4833: the country list is now ONE flat list, sorted by the localized
  // country name under the active UI locale (see compareCountryNames). The old
  // "4 priority markets pinned on top + separator line + the rest A→Z (English
  // collation)" grouping is gone.
  //
  // PRIORITY_ORDER survives ONLY as the last-resort preselection fallback (step
  // (e) of the ladder in resolveDefaultCountry) for the case where no locale,
  // market or customer signal resolves to a served country. It no longer
  // influences the ORDER of the list.
  var FALLBACK_ORDER = ['de', 'lv', 'ee', 'lt'];

  // TASK-3144: the API already returns the DISPLAY NAME per country (Unisend is
  // branded "LP Express" in Lithuania), so we no longer override the name here.
  // Only the LOGO asset id is a frontend concern: LT `unisend` uses the
  // "lpexpress" logo. Any operator/country not listed keeps its own-id logo.
  var LOGO_COUNTRY_OVERRIDE = {
    unisend: { lt: 'lpexpress' }
  };

  var POINTS_PAGE = 500;   // per-page limit (API caps at 1000)

  /* ---- RUNTIME DATA (filled from the live API) ---------------------------- */
  var CONFIG = null;       // { countries: { CC: { methods: [...] } } }
  var AVAILABLE = [];      // TASK-4833: every served ISO code, one flat list
                           // (locale-name-sorted at render time)
  var ratesCache = {};     // iso(lower) → { courier: {price}|null, operators: [...], weightTiers: [...] }

  // TASK-3210: the real cart weight (kg), fetched once from /cart.js at init.
  // Drives the weight-based courier price for the 23 courier-only EU countries
  // (all except DE/LV/LT/EE) — see courierPrice()/loadRates(). Null until loaded;
  // a null/0 weight resolves to the lightest ("<1 kg") tier (a sensible floor).
  var cartWeightKg = null;

  // TASK-3340: cart line-item count, read alongside the weight from the same
  // /cart.js call. Guards against a shopper landing on /pages/delivery-selector
  // directly with an empty cart and clicking "Next" through to checkout. Stays
  // null until the fetch resolves; only a CONFIRMED 0 blocks (fail-open on error).
  var cartItemCount = null;

  // TASK-3539: the live cart subtotal in EUR, read from the same /cart.js call
  // (cart.total_price / 100 — the discounted total, the SAME field the cart's own
  // free-shipping bar compares against, custom-cart-drawer.js). Null until the fetch
  // resolves. Drives the free-shipping override in buildMethods/buildOperators: once
  // it reaches a method's free_shipping_threshold_eur (from /rates), that method
  // ships free (shows EUR 0.00).
  var cartSubtotalEur = null;

  /* ---- STATE ------------------------------------------------------------- */
  var state = {
    country: null,     // ISO-2, lowercase
    method: null,      // 'courier' | 'locker'
    operator: null,    // carrier id (lowercase)
    point: null,       // point id
    // points paging
    points: [],
    pointsTotal: 0,
    pointsSearch: '',
    pointsLoading: false,
    pointsReqSeq: 0    // guards against out-of-order responses
  };

  /* TASK-4846 — ONE global money format on EVERY published locale,
     English included: decimal separator ',' and thousands separator '.'
     (€0,00 / €89,90 / €1.234,56). Deliberately not Intl.NumberFormat or
     toLocaleString: this is a storefront business rule, not a locale
     convention, and en-* would render €1,234.56. Same algorithm as
     moneyFromCents() in custom-cart-drawer.js. Presentation only — the
     numeric value and its rounding are untouched. */
  function eur(n) {
    var v = Number(n);
    if (!isFinite(v)) v = 0;
    /* TASK-5856 — REPORT-5848 D2. `n` is the EUR price_eur from
       GET /api/delivery/rates, and this printed it euro-signed on every market:
       the MANDATORY post-cart step (TASK-5769) told a Swedish shopper "€4,99"
       for a courier Shopify then charged 57 kr for. ILSDeliveryCore.money()
       is the measured, canary-asserted converter that already renders exactly
       these numbers on the delivery card — the same price was being stated in
       two currencies on two live surfaces of one store. It ceils to the
       currency's own granularity (HUF 100) and returns the legacy euro string on
       EUR markets and whenever the oracle refuses. */
    var C = window.ILSDeliveryCore;
    if (C && typeof C.money === 'function') {
      try {
        var s = C.money(v);
        if (typeof s === 'string' && s.length && s !== '—') return s;
      } catch (e) {}
    }
    var sign = v < 0 ? '-' : '';
    var cents = Math.round(Math.abs(v) * 100);
    var frac = String(cents % 100);
    while (frac.length < 2) frac = '0' + frac;
    /* TASK-5946 — display only: a FULLY zero fraction prints as no fraction. */
    return sign + '€' + String(Math.floor(cents / 100)).replace(/\B(?=(\d{3})+(?!\d))/g, '.') + (frac === '00' ? '' : ',' + frac);
  }

  // TASK-3539: free-shipping override. `threshold` is the per-country, per-method
  // free_shipping_threshold_eur from GET /api/delivery/rates (LV courier 80 / locker
  // 35, SE courier 100, ...). When the live cart subtotal (cartSubtotalEur) has
  // reached it, that method ships free. Fully data-driven — no hardcoded thresholds,
  // works for every country. A null threshold (no offer) or unknown subtotal leaves
  // the calculated price untouched.
  function isFreeShip(threshold) {
    /* TASK-5795 — containment. The EUR threshold is not a number that can be
       stated truthfully on a local-currency market (TASK-5784 §1.3 measured the
       mechanism; TASK-5793 proved the gap at a real checkout), so this screen
       shows the price the shopper will actually be charged. */
    if (window.ILSMoney && window.ILSMoney.on() && !window.ILSMoney.mayClaimFreeShipping()) return false;
    /* TASK-5856 — the gate is open, so this screen may flip a method to free
       again on the six. It must not do so on `threshold`: the backend publishes
       free_shipping_threshold_eur = null for all six (TASK-5830 set
       country_free_shipping to NULL), so the comparison below would never fire,
       and if a stale EUR 100 ever came back it would fire at the WRONG number.
       The approved amount, on the EUR axis this function already compares on, is
       the matrix's answer and the Function's actual trigger. */
    var R = window.ILSRewardMatrix;
    if (R && typeof R.freeShipLocalEur === 'function' && R.isFixedLocal()) {
      var fl = R.freeShipLocalEur();
      return fl !== null && cartSubtotalEur != null && cartSubtotalEur >= fl;
    }
    return threshold != null && cartSubtotalEur != null && cartSubtotalEur >= Number(threshold);
  }
  // Price to display for a method: 0 when free-shipping has kicked in, else the
  // calculated price. Formatted by the SAME eur() so the € glyph never moves — only
  // the number changes (eur(0) → "€0,00"). TASK-4846.
  function displayShipPrice(price, threshold) {
    if (price == null) return null;
    return isFreeShip(threshold) ? 0 : price;
  }
  function countryName(iso) { return COUNTRY_NAMES[iso] || iso.toUpperCase(); }

  /* ---- TASK-4833: locale-aware country ordering ---------------------------
     The list is sorted by the LOCALIZED country name under the ACTIVE UI locale,
     not by one hardcoded (English) order. Shopify.locale is the storefront's
     active language ('ru' | 'de' | 'en' | 'lv' | 'lt' | 'et'); documentElement.lang
     is the fallback. Intl.Collator gives the correct alphabet per language
     (Cyrillic for ru, a-umlaut/o-umlaut folding for de/et, c-caron/e-macron for lv/lt).
     If Intl is unavailable we degrade to localeCompare, which is what this file
     did before — never worse than the previous behaviour. */
  var UI_LOCALE = (function () {
    try {
      var l = (window.Shopify && window.Shopify.locale) ||
              (document.documentElement && document.documentElement.lang) || '';
      l = String(l).trim();
      return l || undefined;
    } catch (e) { return undefined; }
  })();
  var COLLATOR = (function () {
    try { return new Intl.Collator(UI_LOCALE, { sensitivity: 'base' }); }
    catch (e) { try { return new Intl.Collator(undefined, { sensitivity: 'base' }); } catch (e2) { return null; } }
  })();
  function compareCountryNames(a, b) {
    var na = countryName(a), nb = countryName(b);
    if (COLLATOR) return COLLATOR.compare(na, nb);
    return na.localeCompare(nb, UI_LOCALE);
  }

  /* ---- TASK-4833: country flags -------------------------------------------
     Flat rectangular flag images from flagcdn.com — the SAME source, ratio and
     box the site already uses for its header language flag (.ils-lang__flag,
     assets/custom-overrides.css) and its delivery-page country carousel
     (.ils-cc__flag, assets/custom-overrides-1910.css). w80 is the header's own
     variant. NO emoji flags: an emoji flag renders as a waving glyph (or as two
     letters on Windows) and would be a second, inconsistent style. */
  var FLAG_CDN_BASE = 'https://flagcdn.com/w80/';
  function flagEl(iso) {
    var span = document.createElement('span');
    span.className = 'ils-dsel__flag';
    span.setAttribute('aria-hidden', 'true');
    var img = document.createElement('img');
    img.src = FLAG_CDN_BASE + String(iso).toLowerCase() + '.png';
    img.alt = '';
    img.loading = 'lazy';
    span.appendChild(img);
    return span;
  }

  /* ---- TASK-3210: weight-based courier price -----------------------------
     For the 23 courier-only EU countries the real courier tariff is a curve by
     order weight (FedEx, per TASK-3176/3182), NOT the single flat price that
     /api/delivery/rates returns (that flat number — e.g. €20 for SE — was the
     old fixed shipping-zone tariff and is what used to show here). We fetch the
     per-country weight curve from /api/delivery/weight-rates (same endpoint the
     "Delivery to <country>" pages use, TASK-3175/3177) and pick the tier for the
     ACTUAL cart weight. DE/LV/LT/EE return an empty curve there (they keep their
     local flat courier tariff), so those transparently fall back to /rates. */
  // Convert the weight-rates `rates` array to {to, price} tiers. `weight_kg` is
  // the tier's upper bound; the null "<1" bucket becomes an ~0.99 kg tier. Same
  // mapping as custom-delivery-country.liquid ratesToTiers() (TASK-3177).
  function weightTiers(rates) {
    if (!rates || !rates.length) return [];
    return rates.map(function (r) {
      return { to: (r.weight_kg == null ? 0.99 : Number(r.weight_kg)), price: Number(r.client_price) };
    }).filter(function (t) { return !isNaN(t.to) && !isNaN(t.price); })
      .sort(function (a, b) { return a.to - b.to; });
  }
  // First tier whose upper bound covers `kg`; heaviest tier for anything above
  // the top bucket (>30 kg). Null when there are no tiers.
  function priceForWeight(tiers, kg) {
    for (var i = 0; i < tiers.length; i++) { if (kg <= tiers[i].to) return tiers[i].price; }
    return tiers.length ? tiers[tiers.length - 1].price : null;
  }
  // The courier price to show for a country: weight-based when a curve exists
  // (the 23 FedEx countries), else the flat /rates courier tariff (DE/LV/LT/EE).
  function courierPrice(rc) {
    if (rc && rc.weightTiers && rc.weightTiers.length) {
      var p = priceForWeight(rc.weightTiers, cartWeightKg == null ? 0 : cartWeightKg);
      if (p != null) return p;
    }
    return rc && rc.courier ? rc.courier.price : null;
  }

  function logoIdFor(carrier, iso) {
    var ov = LOGO_COUNTRY_OVERRIDE[carrier] && LOGO_COUNTRY_OVERRIDE[carrier][iso];
    return ov || carrier;
  }
  function pointAddress(p) {
    return [p.street, p.city].filter(Boolean).join(', ');
  }
  function pointById(id) {
    for (var i = 0; i < state.points.length; i++) {
      if (String(state.points[i].id) === String(id)) return state.points[i];
    }
    return null;
  }
  function currentOperator() {
    var ops = (ratesCache[state.country] || {}).operators || [];
    for (var i = 0; i < ops.length; i++) { if (ops[i].id === state.operator) return ops[i]; }
    return null;
  }
  function operatorAvailable(id) {
    var ops = (ratesCache[state.country] || {}).operators || [];
    for (var i = 0; i < ops.length; i++) { if (ops[i].id === id) return true; }
    return false;
  }
  function countryAvailable(iso) {
    if (!CONFIG || !CONFIG.countries || !iso) return false;
    var keys = Object.keys(CONFIG.countries);
    for (var i = 0; i < keys.length; i++) { if (keys[i].toLowerCase() === String(iso).toLowerCase()) return true; }
    return false;
  }

  /* ---- TASK-3220: persist the in-progress selection across a page refresh and
     browser back / bfcache. sessionStorage is tab-scoped: it survives F5 and a
     cold "back" navigation but clears when the tab closes, so a shopper never
     loses their country / method / operator / point progress mid-flow (the old
     behaviour reset everything to the default country on any reload). The saved
     blob is replayed by restoreSelection() at INIT. saveState() is called from
     the single syncSteps() choke point every selection funnels through; it is a
     no-op while `restoring` is true so replaying a saved state can't re-write it
     with half-built intermediate values. */
  var STATE_KEY = 'ILS_DSEL_STATE_V1';
  var restoring = false;   // true while a saved selection is being replayed
  // TASK-4833: true once the shopper has picked a country from the dropdown
  // themselves. Persisted as `manual` so an explicit choice outranks every
  // computed signal on later page views (step (a) of the preselection ladder).
  var manualCountry = false;
  function saveState() {
    if (restoring) return;
    try {
      var pt = pointById(state.point);
      sessionStorage.setItem(STATE_KEY, JSON.stringify({
        manual: manualCountry,   // TASK-4833 — explicit pick vs computed default
        shop: SHOP_COUNTRY,      // TASK-5769 — the store country this selection was made UNDER
        country: state.country,
        method: state.method,
        operator: state.operator,
        point: state.point,
        pointObj: pt ? { id: pt.id, display_name: pt.display_name, street: pt.street, city: pt.city } : null
      }));
    } catch (e) { /* private mode / quota — persistence is best-effort */ }
  }
  function loadSavedState() {
    try { return JSON.parse(sessionStorage.getItem(STATE_KEY) || 'null'); }
    catch (e) { return null; }
  }

  // Thin JSON GET helper — rejects on any non-2xx so callers land in .catch().
  function apiGet(path) {
    return fetch(API_BASE + path, { headers: { 'Accept': 'application/json' } })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      });
  }

  // TASK-3144: smoothly scroll a just-revealed block into view. The small delay
  // lets the new content render/measure first, so the appearance + scroll read
  // as one motion (and lands on the correct final position, not a stale one).
  function smoothScrollIntoView(el, block) {
    if (restoring) return;   // TASK-3220: no autoscroll while replaying a saved state
    if (!el) return;
    setTimeout(function () {
      // TASK-3350: pin inline:'nearest' so iOS Safari does NOT pan the page
      // horizontally while it scrolls the revealed block into view (the method →
      // operator reveal was leaving the page draggable sideways on iPhone).
      try { el.scrollIntoView({ behavior: 'smooth', block: block || 'start', inline: 'nearest' }); }
      catch (e) { if (el.scrollIntoView) el.scrollIntoView(); }
    }, 90);
  }

  // TASK-3169: a growable spacer appended below the content. `scrollIntoView`
  // can only push the point block up while there is scrollable room BELOW it; on
  // this page the block is the last flow content, so once the operator list is
  // long the block cannot be scrolled clear of the fixed footer and the 319px
  // list ends up half-hidden under the Back/Next bar. This spacer lends exactly
  // as much extra scroll height as a given reposition needs, then collapses back
  // to 0 when the expanded list is no longer shown (so the resting page keeps no
  // dead space). Created lazily so it never exists before the point step opens.
  var _spacer = null;
  function scrollSpacer() {
    if (!_spacer) {
      var inner = root.querySelector('.ils-dsel__inner');
      if (!inner) return null;
      _spacer = document.createElement('div');
      _spacer.className = 'ils-dsel__scroll-spacer';
      _spacer.setAttribute('aria-hidden', 'true');
      inner.appendChild(_spacer);
    }
    return _spacer;
  }
  function setScrollSpacer(px) { var s = scrollSpacer(); if (s) s.style.height = Math.max(0, px) + 'px'; }
  function clearScrollSpacer() { if (_spacer) _spacer.style.height = '0px'; }

  // Is the searchable/expanded point list currently on screen? (Not the collapsed
  // "selected point" summary.) Used to decide whether a keyboard-close should
  // re-run the reposition.
  function pointListVisible() {
    return !!(stepPoint && !stepPoint.hidden && elPointExpand && !elPointExpand.hidden);
  }

  // TASK-3167/3169: scroll the WHOLE point step (search field + the now-populated
  // list window) into view, but only AFTER the real rows have been laid out.
  // Under the live API (TASK-3163) the point list is filled asynchronously, so a
  // fixed timer could fire while only the short loading-spinner row was present
  // (~50px) instead of the filled 319px window. The double requestAnimationFrame
  // waits for the browser to complete layout/paint with the real rows in the DOM
  // before we measure and scroll.
  //
  // TASK-3169: replaced the plain scrollIntoView({block:'start'}) — which ignores
  // the fixed footer and silently stops at max scroll (leaving the list under the
  // bar) — with a footer-aware MINIMAL reveal. We keep the approved TASK-3167
  // layout (the operator context stays visible above the list, no big void) and
  // scroll only as much as needed so the whole block sits inside the band between
  // the top of the screen and the LIVE footer rect (measured, so it includes the
  // footer's real height + env(safe-area-inset-bottom) on real phones — the exact
  // thing that pushed the list under the bar and is invisible to --dsel-foot-h).
  //   • block bottom under the footer → scroll up just enough to clear it;
  //   • block top above the screen     → scroll down just enough to show it;
  //   • already fully inside the band   → don't move (keeps context, no jump).
  // A block taller than the band (tiny screen) aligns its bottom to the footer so
  // the bottom is never hidden. When clearing requires scrolling past the page's
  // natural end (block is the last content, e.g. after a safe-area inset ate the
  // clearance) we lend exactly the missing scroll height via the spacer, then
  // reposition on the next frame once scrollHeight has grown.
  function scrollPointBlockIntoView() {
    if (restoring) return;   // TASK-3220: no autoscroll while replaying a saved state
    if (!stepPoint || stepPoint.hidden) return;
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        // Measure against the no-spacer baseline (reading layout below flushes it).
        clearScrollSpacer();
        var vh = window.innerHeight;
        var rect = stepPoint.getBoundingClientRect();
        var footerTop = elFooter ? elFooter.getBoundingClientRect().top : vh;
        var TOP_GAP = 12;      // min breathing room above the block
        var BOTTOM_GAP = 10;   // gap the list keeps above the footer
        var limitBottom = footerTop - BOTTOM_GAP;
        var blockH = rect.height;
        var band = limitBottom - TOP_GAP;
        // delta = how far to scroll DOWN (content moves up); negative = scroll up.
        var delta = 0;
        if (blockH > band) {
          delta = rect.bottom - limitBottom;              // too tall: pin bottom to footer
        } else if (rect.bottom > limitBottom) {
          delta = rect.bottom - limitBottom;              // under footer: raise to clear it
        } else if (rect.top < TOP_GAP) {
          delta = rect.top - TOP_GAP;                      // above top: lower into view
        }
        if (Math.abs(delta) < 1) return;                  // already seated — no jump
        var targetY = Math.max(0, window.scrollY + delta);
        var docH = Math.max(document.documentElement.scrollHeight, document.body.scrollHeight);
        var maxScroll = Math.max(0, docH - vh);
        if (targetY > maxScroll) {
          // Not enough room below the block — lend the missing scroll height, then
          // reposition on the next frame once scrollHeight has grown.
          setScrollSpacer(Math.ceil(targetY - maxScroll) + 4);
          requestAnimationFrame(function () { window.scrollTo({ top: targetY, behavior: 'smooth' }); });
        } else {
          window.scrollTo({ top: targetY, behavior: 'smooth' });
        }
      });
    });
  }

  /* ---- ELEMENT REFS ----------------------------------------------------- */
  var elCountryWrap   = root.querySelector('[data-ils-country]');
  var elCountryBtn    = root.querySelector('[data-ils-country-btn]');
  var elCountryName   = root.querySelector('[data-ils-country-name]');
  var elCountryPop    = root.querySelector('[data-ils-country-pop]');
  var elCountrySearch = root.querySelector('[data-ils-country-search]');
  var elCountryList   = root.querySelector('[data-ils-country-list]');

  var stepMethod   = root.querySelector('[data-step="method"]');
  var elMethods    = root.querySelector('[data-ils-methods]');
  var stepOperator = root.querySelector('[data-step="operator"]');
  var elOperators  = root.querySelector('[data-ils-operators]');
  var stepPoint    = root.querySelector('[data-step="point"]');
  var elPointSearch= root.querySelector('[data-ils-point-search]');
  var elPoints     = root.querySelector('[data-ils-points]');
  // TASK-3144: collapsed "selected point" summary + the collapsible search/list.
  var elPointExpand       = root.querySelector('[data-ils-point-expand]');
  var elPointSelected     = root.querySelector('[data-ils-point-selected]');
  var elPointSelectedName = root.querySelector('[data-ils-point-selected-name]');
  var elPointSelectedAddr = root.querySelector('[data-ils-point-selected-addr]');

  var elNext = root.querySelector('[data-ils-next]');
  var elBack = root.querySelector('[data-ils-back]');       // widget step-back (footer)
  var elFooter = root.querySelector('.ils-dsel__footer');   // TASK-3169: fixed Back/Next bar (measured for scroll math)

  /* ---- STATUS / SPINNER HELPERS (TASK-3163) ------------------------------
     Small, self-contained loading + error rows. Loading is barely visible when
     the API answers fast; on a failure the shopper gets a message + a retry
     link instead of a dead end. Styles are additive (.ils-dsel__status/
     __spinner/__retry/__points-foot in the CSS). */
  function spinnerEl() {
    var s = document.createElement('span');
    s.className = 'ils-dsel__spinner';
    s.setAttribute('aria-hidden', 'true');
    return s;
  }
  function retryBtn(retryFn) {
    var b = document.createElement('button');
    b.type = 'button';
    b.className = 'ils-dsel__retry';
    b.textContent = I18N.retry;
    b.addEventListener('click', function () { if (retryFn) retryFn(); });
    return b;
  }
  // Block-level status used inside the method area (loading / error).
  function statusBlock(kind, retryFn) {
    var div = document.createElement('div');
    div.className = 'ils-dsel__status';
    if (kind === 'loading') {
      div.appendChild(spinnerEl());
      div.appendChild(document.createTextNode(I18N.loading));
    } else {
      div.appendChild(document.createTextNode(I18N.error + ' '));
      div.appendChild(retryBtn(retryFn));
    }
    return div;
  }

  /* ---- STEP 1: COUNTRY DROPDOWN ---------------------------------------- */
  elCountrySearch.setAttribute('placeholder', I18N.searchCountry);

  function renderCountryList(filter) {
    var q = (filter || '').trim().toLowerCase();
    elCountryList.innerHTML = '';

    function makeOpt(iso) {
      var li = document.createElement('li');
      li.className = 'ils-dsel__country-opt' + (iso === state.country ? ' is-selected' : '');
      li.setAttribute('role', 'option');
      li.setAttribute('data-iso', iso);
      // TASK-4833: flag immediately before the country name. The name lives in its
      // own span so the row can be a flex line without changing the row's type.
      li.appendChild(flagEl(iso));
      var nameEl = document.createElement('span');
      nameEl.className = 'ils-dsel__country-opt-name';
      nameEl.textContent = countryName(iso);
      li.appendChild(nameEl);
      li.addEventListener('click', function () { interacted = true; manualCountry = true; /* TASK-4833: an explicit tap is the strongest preselection signal — see the ladder in resolveDefaultCountry. */ /* TASK-3418: clear the heavy country list BEFORE closing the popup so the browser has nothing left to repaint at hidden=true (kills the residual clipped/white frame). Safe: openCountry() re-renders via renderCountryList(''). */ elCountryList.innerHTML = ''; selectCountry(iso); closeCountry(); });
      return li;
    }
    function matches(iso) { return !q || countryName(iso).toLowerCase().indexOf(q) !== -1; }

    // TASK-4833: ONE list. No priority block, no separator. Sorted by the
    // LOCALIZED name under the active UI locale, so ru-* reads А→Я, de-* folds
    // Ö with O, lv-* orders Č after C, and so on.
    AVAILABLE.filter(matches).sort(compareCountryNames).forEach(function (iso) {
      elCountryList.appendChild(makeOpt(iso));
    });
  }

  function openCountry() {
    elCountryPop.hidden = false;
    elCountryWrap.classList.add('is-open');
    elCountryBtn.setAttribute('aria-expanded', 'true');
    elCountrySearch.value = '';
    renderCountryList('');
    // TASK-3140: do NOT auto-focus the search field. On mobile an auto-focus
    // pops the keyboard over the country list before the shopper can read it.
    // The keyboard/cursor should appear only if the user taps the field.
  }
  function closeCountry() {
    elCountryPop.hidden = true;
    elCountryWrap.classList.remove('is-open');
    elCountryBtn.setAttribute('aria-expanded', 'false');
  }
  elCountryBtn.addEventListener('click', function () {
    if (elCountryPop.hidden) openCountry(); else closeCountry();
  });
  elCountrySearch.addEventListener('input', function () { renderCountryList(elCountrySearch.value); });
  document.addEventListener('click', function (e) {
    if (!elCountryWrap.contains(e.target)) closeCountry();
  });

  // TASK-3163: choosing a country loads its real rates (courier price +
  // per-operator locker prices), then builds the method list. Invalidates every
  // downstream choice, mirrors the mock's reset, and shows a brief loading state
  // while the /rates request is in flight.
  function selectCountry(iso) {
    state.country = iso;
    state.method = null;
    state.operator = null;
    state.point = null;
    resetPoints();
    elCountryName.textContent = countryName(iso);
    syncSteps();
    showMethodsLoading();

    // TASK-3220: return the chain so restoreSelection() can await the rates load
    // before it replays the saved method/operator/point.
    return loadRates(iso).then(function () {
      if (state.country !== iso) return;   // country changed while loading
      buildMethods();
      syncSteps();
    }).catch(function () {
      if (state.country !== iso) return;
      showMethodsError(function () { selectCountry(iso); });
    });
  }

  // Fetch + cache the rates for a country. Splits the /rates rows into the
  // courier tariff and the list of locker operators (id / display name / price /
  // logo id), which drive both the method cards and the operator list.
  function loadRates(iso) {
    if (ratesCache[iso]) return Promise.resolve(ratesCache[iso]);
    var uc = encodeURIComponent(iso.toUpperCase());
    // TASK-3210: fetch the flat /rates matrix AND the weight curve together. The
    // weight-rates leg is optional — a failure/absence just leaves weightTiers
    // empty and the courier price falls back to the flat /rates tariff.
    return Promise.all([
      apiGet('/api/delivery/rates?country=' + uc),
      apiGet('/api/delivery/weight-rates?country=' + uc).catch(function () { return null; })
    ]).then(function (res) {
        var data = res[0], wr = res[1];
        var courier = null;
        var operators = [];
        (data && data.rates ? data.rates : []).forEach(function (r) {
          if (r.type === 'courier') {
            // TASK-3539: carry the per-row free_shipping_threshold_eur so the method
            // card can show EUR 0.00 once the cart subtotal reaches it.
            courier = { price: r.price_eur, threshold: r.free_shipping_threshold_eur };
          } else if (r.type === 'locker') {
            operators.push({
              id: r.carrier,
              name: r.display_name || r.carrier,
              price: r.price_eur,
              threshold: r.free_shipping_threshold_eur,  // TASK-3539
              logoId: logoIdFor(r.carrier, iso)
            });
          }
        });
        ratesCache[iso] = {
          courier: courier,
          operators: operators,
          weightTiers: weightTiers(wr && wr.rates)   // TASK-3210: [] for DE/LV/LT/EE
        };
        return ratesCache[iso];
      });
  }

  function showMethodsLoading() {
    stepMethod.hidden = false;
    elMethods.innerHTML = '';
    elMethods.appendChild(statusBlock('loading'));
  }
  function showMethodsError(retryFn) {
    stepMethod.hidden = false;
    elMethods.innerHTML = '';
    elMethods.appendChild(statusBlock('error', retryFn));
  }

  /* ---- STEP 2: METHOD --------------------------------------------------- */
  // TASK-3163: methods come from the cached rates — courier if the country has a
  // courier tariff, locker if it has at least one enabled locker operator.
  function buildMethods() {
    elMethods.innerHTML = '';
    var rc = ratesCache[state.country] || { courier: null, operators: [] };

    // TASK-3210: courier price is weight-based for the 23 FedEx countries,
    // flat /rates tariff for DE/LV/LT/EE (courierPrice() picks the right source).
    // TASK-3539: courier price honours its free_shipping_threshold_eur (EUR 0.00 when reached).
    if (rc.courier) elMethods.appendChild(methodCard('courier', displayShipPrice(courierPrice(rc), rc.courier.threshold)));
    if (rc.operators.length) elMethods.appendChild(methodCard('locker', null));

    // Nothing available for this country (shouldn't happen for a listed country).
    if (!rc.courier && !rc.operators.length) {
      var empty = document.createElement('div');
      empty.className = 'ils-dsel__status';
      empty.textContent = I18N.noPoints;
      elMethods.appendChild(empty);
      return;
    }

    // Auto-select courier when it's the only method (no extra tap for the common
    // courier-only country case, e.g. Germany).
    if (!rc.operators.length && rc.courier) {
      state.method = 'courier';
      markSelected(elMethods, 'courier');
    }
  }

  // Labels/descriptions for methods come from data attributes rendered by
  // Liquid `| t`. We read them from hidden template strings on first use.
  function methodLabels() {
    if (methodLabels._c) return methodLabels._c;
    methodLabels._c = {
      courier: {
        name: root.getAttribute('data-i18n-courier') || 'Courier',
        desc: root.getAttribute('data-i18n-courier-desc') || 'To your door'
      },
      locker: {
        name: root.getAttribute('data-i18n-locker') || 'Parcel locker',
        desc: root.getAttribute('data-i18n-locker-desc') || 'Pick up from a locker near you'
      }
    };
    return methodLabels._c;
  }

  function methodCard(kind, price) {
    var L = methodLabels()[kind];
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'ils-dsel__opt';
    btn.setAttribute('data-value', kind);
    btn.innerHTML =
      '<span class="ils-dsel__opt-radio" aria-hidden="true"></span>' +
      '<span class="ils-dsel__opt-main">' +
        '<span class="ils-dsel__opt-name"></span>' +
        '<span class="ils-dsel__opt-desc"></span>' +
      '</span>' +
      (price != null ? '<span class="ils-dsel__opt-price"></span>' : '');
    btn.querySelector('.ils-dsel__opt-name').textContent = L.name;
    btn.querySelector('.ils-dsel__opt-desc').textContent = L.desc;
    if (price != null) btn.querySelector('.ils-dsel__opt-price').textContent = eur(price);
    btn.addEventListener('click', function () { interacted = true; selectMethod(kind); });
    return btn;
  }

  function selectMethod(kind) {
    state.method = kind;
    state.operator = null;
    state.point = null;
    markSelected(elMethods, kind);
    if (kind === 'locker') buildOperators();
    syncSteps();
    // TASK-3144 (autoscroll 1/2): choosing the locker reveals the operator list
    // below — scroll it into view so "Choose a parcel operator" is visible.
    if (kind === 'locker') smoothScrollIntoView(stepOperator, 'start');
  }

  /* ---- STEP 3: OPERATORS ------------------------------------------------ */
  // TASK-3140: markup for the operator's leading logo. Real logo when Liquid
  // supplied a URL for that id; otherwise a coloured letter avatar so a single
  // missing file never blocks the rest of the row. `op.logoId` already reflects
  // any per-country brand override (e.g. unisend → lpexpress in LT).
  function operatorLogoHTML(op) {
    if (OP_LOGOS[op.logoId]) {
      return '<span class="ils-dsel__op-logo-box"><img class="ils-dsel__op-logo" ' +
             'loading="lazy" decoding="async"></span>';
    }
    var letter = (op.name || '?').charAt(0).toUpperCase();
    return '<span class="ils-dsel__op-logo-fallback" aria-hidden="true">' + letter + '</span>';
  }

  // TASK-3163: operators come from the cached rates for the current country, so
  // the display name (incl. LT "LP Express") and price are the live values.
  function buildOperators() {
    elOperators.innerHTML = '';
    var ops = (ratesCache[state.country] || {}).operators || [];
    ops.forEach(function (op) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'ils-dsel__opt';
      btn.setAttribute('data-value', op.id);
      btn.innerHTML =
        '<span class="ils-dsel__opt-radio" aria-hidden="true"></span>' +
        '<span class="ils-dsel__opt-main ils-dsel__opt-main--op">' +
          operatorLogoHTML(op) +
          '<span class="ils-dsel__opt-name"></span>' +
        '</span>' +
        '<span class="ils-dsel__opt-price"></span>';
      btn.querySelector('.ils-dsel__opt-name').textContent = op.name;
      // TASK-3539: operator price honours its free_shipping_threshold_eur (EUR 0.00 when reached).
      btn.querySelector('.ils-dsel__opt-price').textContent = (op.price != null ? eur(displayShipPrice(op.price, op.threshold)) : '');
      // TASK-3140: fill the real logo <img> src if one was provided (kept out of
      // innerHTML to avoid injecting a URL string into markup).
      var img = btn.querySelector('.ils-dsel__op-logo');
      if (img) { img.setAttribute('src', OP_LOGOS[op.logoId]); img.setAttribute('alt', op.name); }
      btn.addEventListener('click', function () { interacted = true; selectOperator(op.id); });
      elOperators.appendChild(btn);
    });
  }

  function selectOperator(id) {
    state.operator = id;
    state.point = null;
    markSelected(elOperators, id);
    resetPointView();          // TASK-3144: fresh operator → point list starts expanded
    elPointSearch.value = '';
    resetPoints();
    var pointsP = loadPoints(true, true);    // TASK-3163: fetch first page; TASK-3167: scroll AFTER render
    syncSteps();
    // TASK-3145 (autoscroll 3/3): choosing a concrete operator reveals the point
    // search field + the expanded list below it.
    // TASK-3167: this is the FIRST of two scrolls. It brings the loading state
    // into view immediately (so the shopper sees the fetch happening); the second
    // scroll fires from loadPoints() once the real point rows have rendered, so
    // the final full-height list is reliably in view even on a slow API response.
    smoothScrollIntoView(stepPoint, 'start');
    // TASK-3220: hand back the points fetch so restoreSelection() can wait for
    // the first page before replaying a saved point.
    return pointsP || Promise.resolve();
  }

  /* ---- STEP 4: POINTS + LIVE SEARCH / PAGINATION (TASK-3163) -------------
     Points are fetched from the API, not filtered client-side:
       • operator select / search change → loadPoints(true): fresh first page.
       • scroll near the bottom of the fixed window → loadPoints(false): append
         the next `offset` page (only while more remain).
     Server-side trigram `search=` means a term matches across the WHOLE point
     set, not just the rows already loaded. A monotonically increasing request
     sequence discards any out-of-order response (fast typing / operator swap). */
  function resetPoints() {
    state.points = [];
    state.pointsTotal = 0;
    state.pointsSearch = '';
    state.pointsLoading = false;
    state.pointsReqSeq++;   // invalidate any in-flight request
    clearScrollSpacer();    // TASK-3169: leaving the point list drops the lent room
  }

  function pointsUrl(search, offset) {
    return '/api/delivery/points' +
      '?country=' + encodeURIComponent(state.country.toUpperCase()) +
      '&carrier=' + encodeURIComponent(state.operator) +
      '&limit=' + POINTS_PAGE +
      '&offset=' + offset +
      (search ? '&search=' + encodeURIComponent(search) : '');
  }

  function makePointRow(p) {
    var li = document.createElement('li');
    li.className = 'ils-dsel__point' + (String(p.id) === String(state.point) ? ' is-selected' : '');
    li.setAttribute('data-id', p.id);
    li.innerHTML =
      '<span class="ils-dsel__point-radio" aria-hidden="true"></span>' +
      '<span class="ils-dsel__point-body">' +
        '<span class="ils-dsel__point-name"></span>' +
        '<span class="ils-dsel__point-addr"></span>' +
      '</span>';
    li.querySelector('.ils-dsel__point-name').textContent = p.display_name || '';
    li.querySelector('.ils-dsel__point-addr').textContent = pointAddress(p);
    li.addEventListener('click', function () { interacted = true; selectPoint(p.id); });
    return li;
  }

  // Full-list message row (loading / empty / error) used when the list is empty.
  function pointsMessage(kind, retryFn) {
    var li = document.createElement('li');
    li.className = 'ils-dsel__points-empty';
    if (kind === 'loading') {
      li.appendChild(spinnerEl());
      li.appendChild(document.createTextNode(I18N.loading));
    } else if (kind === 'error') {
      li.appendChild(document.createTextNode(I18N.error + ' '));
      li.appendChild(retryBtn(retryFn));
    } else {
      li.textContent = I18N.noPoints;
    }
    return li;
  }
  // Footer row appended below already-loaded rows during pagination.
  function pointsFoot(kind, retryFn) {
    var li = document.createElement('li');
    li.className = 'ils-dsel__points-foot';
    if (kind === 'loading') {
      li.appendChild(spinnerEl());
      li.appendChild(document.createTextNode(I18N.loading));
    } else {
      li.appendChild(document.createTextNode(I18N.error + ' '));
      li.appendChild(retryBtn(retryFn));
    }
    return li;
  }
  function removePointsFoot() {
    var f = elPoints.querySelector('.ils-dsel__points-foot');
    if (f) f.parentNode.removeChild(f);
  }

  // TASK-3167: `scrollAfter` (only passed on operator select) triggers a scroll
  // of the point step into view AFTER the reset render lands its real rows — see
  // scrollPointBlockIntoView(). Not set for search re-queries (scrolling mid-typing
  // would be jarring) or pagination appends.
  function loadPoints(reset, scrollAfter) {
    // TASK-3220: always return a promise so callers (selectOperator during a
    // saved-state replay) can await the fetch.
    if (!state.country || !state.operator) return Promise.resolve();
    if (state.pointsLoading) return Promise.resolve();
    if (!reset && state.points.length >= state.pointsTotal && state.pointsTotal > 0) return Promise.resolve();

    state.pointsLoading = true;
    var seq = ++state.pointsReqSeq;
    var search = state.pointsSearch;
    var offset = reset ? 0 : state.points.length;

    if (reset) {
      elPoints.innerHTML = '';
      elPoints.appendChild(pointsMessage('loading'));
    } else {
      removePointsFoot();
      elPoints.appendChild(pointsFoot('loading'));
    }

    return apiGet(pointsUrl(search, offset)).then(function (data) {
      if (seq !== state.pointsReqSeq) return;   // superseded by a newer request
      state.pointsLoading = false;
      state.pointsTotal = (data && data.total) || 0;
      var got = (data && data.points) || [];

      if (reset) {
        elPoints.innerHTML = '';
        state.points = got;
        if (!got.length) { elPoints.appendChild(pointsMessage('empty')); }
        else { got.forEach(function (p) { elPoints.appendChild(makePointRow(p)); }); }
        // TASK-3167: real rows (or the empty message) are now in the DOM — scroll
        // the whole step into view against the FINAL height, not the loading stub.
        // TASK-3169: footer-aware reposition (list bottom kept above Back/Next).
        if (scrollAfter) scrollPointBlockIntoView();
      } else {
        removePointsFoot();
        state.points = state.points.concat(got);
        got.forEach(function (p) { elPoints.appendChild(makePointRow(p)); });
      }
    }).catch(function () {
      if (seq !== state.pointsReqSeq) return;
      state.pointsLoading = false;
      if (reset) {
        elPoints.innerHTML = '';
        elPoints.appendChild(pointsMessage('error', function () { loadPoints(true, scrollAfter); }));
        // TASK-3167: still scroll so the (retryable) error state is visible, not
        // left below the fold after the operator tap.
        if (scrollAfter) scrollPointBlockIntoView();
      } else {
        removePointsFoot();
        elPoints.appendChild(pointsFoot('error', function () { loadPoints(false); }));
      }
    });
  }

  // Infinite scroll: near the bottom of the fixed 5.5-row window, pull the next
  // page (only while more points remain and no request is already running).
  elPoints.addEventListener('scroll', function () {
    if (state.pointsLoading) return;
    if (state.points.length >= state.pointsTotal) return;
    if (elPoints.scrollTop + elPoints.clientHeight >= elPoints.scrollHeight - 48) {
      loadPoints(false);
    }
  });

  // TASK-3163: debounced server-side search. Typing re-queries the API (trigram
  // match over name/street/city) instead of filtering the loaded page, so terms
  // find points anywhere in the full set.
  var searchTimer = null;
  elPointSearch.addEventListener('input', function () {
    var v = elPointSearch.value;
    clearTimeout(searchTimer);
    searchTimer = setTimeout(function () {
      state.pointsSearch = (v || '').trim();
      loadPoints(true);
    }, 250);
  });

  /* ---- TASK-3169: re-seat the list once the on-screen keyboard is dismissed ----
     On mobile, tapping the point-search field opens the keyboard, which shrinks
     the visible viewport (and the browser scrolls the input into view). When the
     keyboard is dismissed the viewport grows back, but the list is left wherever
     the keyboard-scroll parked it — often with its bottom back under the footer.
     visualViewport 'resize' is the reliable cross-browser signal for keyboard
     show/hide (it fires with the new visible height); a `blur` on the field is a
     secondary trigger for browsers/paths where the viewport event is flaky. In
     both cases we only reposition when the expanded list is actually on screen. */
  if (window.visualViewport) {
    var _lastVVH = window.visualViewport.height;
    window.visualViewport.addEventListener('resize', function () {
      var h = window.visualViewport.height;
      var grew = (h - _lastVVH) > 80;   // viewport expanded ⇒ keyboard likely closed
      _lastVVH = h;
      if (grew && pointListVisible()) scrollPointBlockIntoView();
    });
  }
  elPointSearch.addEventListener('blur', function () {
    // Let the viewport/layout settle after the keyboard animates away.
    setTimeout(function () { if (pointListVisible()) scrollPointBlockIntoView(); }, 250);
  });

  function selectPoint(id) {
    state.point = id;
    markPointSelected(id);
    syncSteps();
    // TASK-3144: mirror the country dropdown — once a point is chosen, collapse
    // the search + full list into a compact accented summary row.
    collapsePoint();
    // TASK-3144 (autoscroll 2/2): reveal the whole completed path down to Next.
    smoothScrollIntoView(elPointSelected, 'center');
  }

  function markPointSelected(id) {
    var items = elPoints.querySelectorAll('.ils-dsel__point');
    for (var i = 0; i < items.length; i++) {
      items[i].classList.toggle('is-selected', items[i].getAttribute('data-id') === String(id));
    }
  }

  /* ---- TASK-3144: collapse / expand the point list (country-dropdown UX) ---- */
  function collapsePoint() {
    var p = pointById(state.point);
    if (!p) return;
    elPointSelectedName.textContent = p.display_name || '';
    elPointSelectedAddr.textContent = pointAddress(p);
    elPointExpand.hidden = true;
    elPointSelected.hidden = false;
    elPointSelected.setAttribute('aria-expanded', 'false');
    clearScrollSpacer();   // TASK-3169: list is collapsed → drop the lent scroll room
  }
  function expandPoint() {
    elPointSelected.hidden = true;
    elPointSelected.setAttribute('aria-expanded', 'true');
    elPointExpand.hidden = false;
    // The loaded list is still in the DOM — just re-mark the current choice.
    markPointSelected(state.point);
    // TASK-3150: same autoscroll as selectOperator() — bring the re-expanded
    // search + list into view so it isn't left off the bottom of the screen.
    // TASK-3167: the points are already loaded (no async fetch), but the list
    // container was just un-hidden, so scroll AFTER layout completes (rAF) rather
    // than on a blind timer, so it lands on the now-full-height list.
    // TASK-3169: footer-aware reposition so the re-expanded list clears Back/Next.
    scrollPointBlockIntoView();
  }
  // Reset to the expanded state with nothing collapsed (used when the point is
  // cleared: operator change / step-back).
  function resetPointView() {
    elPointSelected.hidden = true;
    elPointSelected.setAttribute('aria-expanded', 'false');
    elPointExpand.hidden = false;
    clearScrollSpacer();   // TASK-3169: fresh operator / step-back → no leftover room
  }
  if (elPointSelected) {
    elPointSelected.addEventListener('click', function () { expandPoint(); });
  }

  /* ---- SHARED HELPERS --------------------------------------------------- */
  function markSelected(container, value) {
    var opts = container.querySelectorAll('.ils-dsel__opt');
    for (var i = 0; i < opts.length; i++) {
      opts[i].classList.toggle('is-selected', opts[i].getAttribute('data-value') === value);
    }
  }

  // Show/hide steps according to progress, and enable the CTA when the
  // current path is complete.
  function syncSteps() {
    var showMethod = !!state.country;
    var showOperator = state.method === 'locker';
    var showPoint = state.method === 'locker' && !!state.operator;

    stepMethod.hidden = !showMethod;
    stepOperator.hidden = !showOperator;
    stepPoint.hidden = !showPoint;

    var complete =
      (state.method === 'courier') ||
      (state.method === 'locker' && !!state.operator && !!state.point);
    elNext.disabled = !complete;

    // TASK-3347: "Back" now LEAVES the widget to the cart, so it is available at
    // EVERY step (including the first) — a shopper on step 1 must still be able to
    // return to the cart. (It used to be disabled until a method was chosen, back
    // when it stepped through the internal country→method→operator→point stack.)
    if (elBack) elBack.disabled = false;

    // TASK-3220: every selection funnels through here — persist the current
    // choice so a refresh / cold-back can restore it (no-op while restoring).
    saveState();

    // TASK-3355: also from this single choke point, pre-write the selection to
    // the cart in the background as soon as it is complete (after a real user
    // interaction), so "Next" → checkout is near-instant. No-op while restoring
    // or before the shopper has interacted.
    schedulePersist();
  }

  // TASK-3347: the old TASK-3137 stepBack() (which walked one level back inside
  // the widget: point → operator → method → country) was removed. The footer
  // "Back" now leaves the page to /cart (see the elBack handler below); every
  // prior choice is still editable IN PLACE — tap the country dropdown, the other
  // method/operator card, or the collapsed point summary to re-open it.

  /* ---- TASK-3157: persist the completed choice to cart attributes, then hand
     off to Shopify checkout. -------------------------------------------------
     UNCHANGED by TASK-3163 except that the source values (country / operator /
     point) are now live API data instead of mock. Still an isolated prototype
     (DEV theme only); the attribute KEYS/FORMAT are identical. */

  // Locale-aware storefront root (e.g. "/en-eu/") so the cart write and the
  // checkout redirect stay in the shopper's market/locale. Falls back to "/".
  function localeRoot() {
    var r = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    return r.charAt(r.length - 1) === '/' ? r : r + '/';
  }

  /* =====================================================================
     TASK-5771 — ONE Ajax-cart route resolver for this file.
     ---------------------------------------------------------------------
     On the multi-country `europe` market — served at /en-eu/ and /ru-eu/, the
     roots Cyprus shops on — EVERY localized Ajax cart route answers 404 while
     the apex route answers 200 for the SAME session and the SAME cart.
     Measured live 2026-10-24 (scripts/task-5771-eudbg.mjs, Dev slot):

         /en-eu/cart/add.js    404     /cart/add.js    200
         /en-eu/cart/update.js 404     /cart/update.js 200
         /en-eu/cart/change.js 404     /cart/change.js 200
         ... byte-identical results on /ru-eu/, and 200 on /en-pl/ and /ru-de/

     REPORT-5769 saw the add.js half of this and parked it as a separate
     diagnostic. It is load-bearing here: this task only hands the country to
     Shopify AFTER the selection is proven on the cart, so on the -eu roots the
     persist never converged and the handoff could not fire at all. It is also
     wider than this task and PRE-EXISTING — with all three writes 404ing, an
     -eu shopper's delivery attributes never reached the cart, so our carrier
     service and the delivery-country validation Function were both running
     blind on that market.

     SCOPE. The localized route stays FIRST, so behaviour on the 25 roots where
     it already works is unchanged and the locale-aware write TASK-3210/3353
     wanted is still what happens. ONLY a 404 degrades to the apex route — a
     422/400 means Shopify REJECTED the payload and must not be retried
     elsewhere. The cart is identified by the `cart` cookie, not by the URL
     prefix, so both routes address the same cart; the prefix only ever decided
     the response's locale. The decision is remembered per page view, so the
     404 is paid once rather than on every write.
     ===================================================================== */
  var cartRouteApex = false;
  function cartFetch(path, init) {
    if (cartRouteApex) return fetch('/' + path, init);
    var localized = localeRoot() + path;
    return fetch(localized, init).then(function (r) {
      if (r.status !== 404) return r;
      cartRouteApex = true;
      try { console.warn('[dsel] ' + localized + ' -> 404; falling back to /' + path + ' (shared-market root)'); } catch (e) {}
      return fetch('/' + path, init);
    });
  }

  // Build the { attributes } payload from the current selection. The courier
  // path clears the locker-only fields (an empty string removes a Shopify cart
  // attribute), so switching courier↔locker never leaves stale carrier/point
  // data on the cart. Format identical to TASK-3157.
  function buildCartAttributes() {
    var attrs = {
      'delivery_country': (state.country || '').toUpperCase(),
      'delivery_type': state.method || '',
      'delivery_carrier': '',
      'delivery_carrier_name': '',
      'delivery_point_id': '',
      'delivery_point_name': '',
      'delivery_point_address': ''
    };
    if (state.method === 'locker') {
      var op = currentOperator();
      var pt = pointById(state.point);
      // Stable internal operator id (e.g. "unisend") for machine filtering, plus
      // the per-country DISPLAY name (e.g. "LP Express" in LT) for any checkout
      // hint text that needs to echo what the shopper picked.
      attrs['delivery_carrier'] = state.operator || '';
      attrs['delivery_carrier_name'] = (op && op.name) || '';
      if (pt) {
        attrs['delivery_point_id'] = String(pt.id);
        attrs['delivery_point_name'] = pt.display_name || '';
        attrs['delivery_point_address'] = pointAddress(pt);
      }
    }
    return attrs;
  }

  /* ---- TASK-3228: also persist the choice to LINE-ITEM PROPERTIES ----------
     TASK-3223 established that cart attributes do NOT survive into the final
     order in our current flow, while line-item properties DO (this reproduces
     the Parcely mechanism, TASK-3225/3226). So, ALONGSIDE the cart-attribute
     write above (kept intact), we stamp the same choice onto every line item.

     Property names are underscore-prefixed on purpose: Shopify hides `_`-prefixed
     line-item properties from the shopper (cart / checkout / confirmation) but
     STILL shows them to the merchant in the Admin order detail — exactly what we
     want (machine data in the order, no clutter for the customer). */
  // TASK-3355: derive the line-item property set from an ALREADY-BUILT attribute
  // object (a frozen snapshot) rather than reading live `state`. The incremental
  // pre-write (see the persist queue below) captures the attrs at write-start and
  // must stamp the SAME frozen values onto the line items, even if the shopper
  // keeps changing the selection while the write is in flight.
  function lineItemPropsFromAttrs(a) {
    return {
      '_delivery_type': a['delivery_type'] || '',
      '_delivery_country': a['delivery_country'] || '',
      '_delivery_carrier': a['delivery_carrier'] || '',
      '_delivery_parcelshop_id': a['delivery_point_id'] || '',
      '_delivery_point_name': a['delivery_point_name'] || '',
      '_delivery_point_address': a['delivery_point_address'] || ''
    };
  }
  function buildLineItemProperties() {
    // Reuse the already-computed attribute values so the two channels can never
    // drift. Courier clears the locker-only fields (empty strings), keeping the
    // property set self-consistent when switching courier <-> locker.
    return lineItemPropsFromAttrs(buildCartAttributes());
  }

  /* Write the delivery properties onto EVERY line item in the cart.
     - /cart/change.js is the only AJAX endpoint that sets line-item `properties`
       (update.js `updates` handles quantities only — verified against the AJAX
       Cart API docs), and it changes ONE line at a time, so we iterate.
     - We apply the SAME properties to all items (task decision: more robust — the
       data reaches the order no matter how Shopify splits/merges multi-item
       orders; duplicating a handful of strings is harmless).
     - Keys: a line item's `key` changes once its properties change, but each
       ORIGINAL key from the /cart.js snapshot is touched exactly once and the
       keys of not-yet-touched lines are unaffected, so a serial pass over the
       snapshot is safe. Quantity is preserved (re-sent) so nothing changes but
       the properties. */
  // TASK-3353 (speed-up): accept an ALREADY-FETCHED cart so the caller can pass
  // the /cart/update.js response (which returns the full cart, incl. items+keys)
  // and we skip a second, redundant /cart.js round-trip on the Next → checkout
  // critical path. Falls back to fetching /cart.js when no snapshot is supplied
  // (keeps the standalone contract). The serial change.js pass is unchanged —
  // Shopify serialises cart mutations per token, so parallel change.js calls can
  // race/lock; serial stays the correct (order-critical) behaviour.
  function writeLineItemProperties(cartSnapshot, propsSnapshot) {
    // TASK-3355: `propsSnapshot` lets the pre-write pass in the frozen property
    // set it is persisting; without it we fall back to the live-state build (the
    // original standalone contract).
    var props = propsSnapshot || buildLineItemProperties();
    var cartP = (cartSnapshot && cartSnapshot.items)
      ? Promise.resolve(cartSnapshot)
      : cartFetch('cart.js', { headers: { 'Accept': 'application/json' } })   /* TASK-5771 */
          .then(function (r) { if (!r.ok) throw new Error('cart.js HTTP ' + r.status); return r.json(); });
    return cartP.then(function (cart) {
        var items = (cart && cart.items) || [];
        var chain = Promise.resolve();
        items.forEach(function (it) {
          chain = chain.then(function () {
            return cartFetch('cart/change.js', {                /* TASK-5771 */
              method: 'POST',
              // TASK-3353: keepalive so a write already in flight is not aborted
              // if the checkout redirect fires before it resolves.
              keepalive: true,
              // TASK-4850 CORE CHANGE D: a _delivery_*-properties-only write at
              // unchanged quantity cannot move the gift tier — see the marker note
              // on persistSelection() above.
              __ilsDeliveryMeta: true,
              headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
              body: JSON.stringify({ id: it.key, quantity: it.quantity, properties: props })
            }).then(function (r) {
              if (!r.ok) throw new Error('change.js HTTP ' + r.status + ' for line ' + it.key);
              return r.json();
            });
          });
        });
        return chain;
      });
  }

  /* ---- TASK-3355: INCREMENTAL / PRE-EMPTIVE CART WRITE -------------------
     Before this task the ENTIRE persistence (cart/update.js for the attributes
     + one serial /cart/change.js per line item for the properties) ran only on
     the "Next" click, so the shopper waited for (1 + N) sequential round-trips
     right before the checkout redirect — a delay that grows with cart size
     (measured in TASK-3353). Here we move that work OFF the critical path: the
     moment a selection is COMPLETE (courier chosen, or locker operator + point
     chosen) we persist it to the cart in the BACKGROUND, so by the time the
     shopper reaches "Next" the data is already on the cart and the redirect is
     effectively instant.

     Design (guards every edge case the task calls out):
       • One SERIALISED write queue (never two persists in flight) → concurrent
         /cart/update.js + /change.js can't race or clobber each other, even if
         the shopper clicks through the steps very fast (edge case 3).
       • Each persist is keyed by a SIGNATURE = the JSON of the final attribute
         set. `writtenSig` records what is confirmed on the cart; `desiredSig`
         is what the current selection wants. Changing the selection (e.g. going
         back to the country step and picking another country) simply moves
         `desiredSig`; the queue converges to it and the full attribute set is
         REWRITTEN (update.js replaces all attrs; the property write overwrites
         every line item), so no stale carrier/point data is ever left behind
         (edge case 1).
       • "Next" calls ensurePersisted(currentSig): it resolves ONLY once the cart
         has been confirmed written for the CURRENT selection — if a background
         write is still in flight, or hasn't run yet, Next waits for (and if
         needed kicks) the write before redirecting, and never sends the shopper
         to checkout with incomplete/stale attributes (edge case 2 — the P0 risk
         guarded in TASK-3267). On a write failure it re-enables Next to retry.
       • Pre-writing only starts after a genuine USER interaction (`interacted`)
         and never during a saved-state replay (`restoring`), so merely landing
         on the page — or a refresh/bfcache restore — does not fire a cart write
         before the shopper has actually chosen anything. */
  var interacted = false;   // set true by the first real user selection (below)

  function isSelectionComplete() {
    return (state.method === 'courier') ||
           (state.method === 'locker' && !!state.operator && !!state.point);
  }
  // Canonical signature of the persistable selection — the exact attribute JSON.
  // Null while the selection is still incomplete (nothing to persist yet).
  function selectionSignature() {
    if (!isSelectionComplete()) return null;
    return JSON.stringify(buildCartAttributes());
  }

  var writeState = {
    desiredSig: null,    // signature the current selection wants persisted
    writtenSig: null,    // signature confirmed written to the cart (attrs + items)
    inFlight: null,      // Promise of the running persist, or null
    inFlightSig: null    // signature the running persist is writing
  };

  // Persist one frozen attribute snapshot: attributes first (single update.js,
  // whose response is the full cart), then the line-item properties for the SAME
  // snapshot (reusing the update.js cart so there is no extra /cart.js GET — the
  // TASK-3353 saving is preserved). keepalive so an in-flight write is not aborted
  // if a navigation fires before it resolves.
  function persistSelection(attrs) {
    var props = lineItemPropsFromAttrs(attrs);
    return cartFetch('cart/update.js', {                       /* TASK-5771 route resolver */
      method: 'POST',
      keepalive: true,
      // TASK-4850 CORE CHANGE D: an attributes-only write cannot move the
      // merchandise subtotal, so custom-gift-logic.js's observer ignores it
      // instead of scheduling a redundant /cart.js reconcile 450 ms later
      // (REPORT-4848 §5 measured one per delivery write).
      __ilsDeliveryMeta: true,
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ attributes: attrs })
    }).then(function (r) {
      if (!r.ok) throw new Error('cart update HTTP ' + r.status);
      return r.json();
    }).then(function (cart) {
      return writeLineItemProperties(cart, props);
    });
  }

  // Start a persist if one is warranted and none is running. On completion it
  // AUTO-CONTINUES to a NEWER desired signature (the shopper changed the choice
  // mid-write), but does NOT hot-loop retrying the SAME just-failed signature —
  // that retry is left to the next user action or the Next click.
  function kickWrite() {
    if (writeState.inFlight) return;
    var sig = writeState.desiredSig;
    if (!sig || sig === writeState.writtenSig) return;
    var attrs;
    try { attrs = JSON.parse(sig); } catch (e) { return; }
    writeState.inFlightSig = sig;
    writeState.inFlight = persistSelection(attrs).then(function () {
      writeState.writtenSig = sig;            // confirmed on the cart
    }, function (err) {
      try { console.error('[dsel] pre-write failed (will retry on Next):', err); } catch (e2) {}
    }).then(function () {
      var done = writeState.inFlightSig;
      writeState.inFlight = null;
      writeState.inFlightSig = null;
      if (writeState.desiredSig &&
          writeState.desiredSig !== writeState.writtenSig &&
          writeState.desiredSig !== done) {
        kickWrite();                          // a NEW target appeared while writing
      }
    });
  }

  // Called from syncSteps() (the single selection choke point). Records the
  // desired signature and, once the shopper has interacted and the selection is
  // complete, kicks the background write.
  function schedulePersist() {
    // TASK-4850: with the fast path ON this choke point drives the readiness
    // engine instead of the TASK-3355 queue (which stays intact below and is
    // exactly what runs when the kill switch is OFF).
    if (FASTPATH) { fpSchedule(); return; }
    if (restoring) return;                    // don't write while replaying saved state
    var sig = selectionSignature();
    writeState.desiredSig = sig;              // may be null (incomplete)
    if (!sig || !interacted) return;
    if (sig === writeState.writtenSig) return;
    kickWrite();
  }

  // Resolve only once `sig` is confirmed written; kick/await writes as needed.
  // Rejects if the selection was superseded or the write keeps failing (Next then
  // re-enables so the shopper can retry). Bounded so a persistently failing
  // backend can never hang the button forever.
  function ensurePersisted(sig) {
    writeState.desiredSig = sig;
    if (!sig || writeState.writtenSig === sig) return Promise.resolve();
    return new Promise(function (resolve, reject) {
      var starts = 0;
      (function loop() {
        if (writeState.writtenSig === sig) { resolve(); return; }
        if (writeState.desiredSig !== sig) { reject(new Error('superseded')); return; }
        if (!writeState.inFlight) {
          if (++starts > 3) { reject(new Error('write not converging')); return; }
          kickWrite();
        }
        (writeState.inFlight || Promise.resolve()).then(loop, loop);
      })();
    });
  }

  /* =====================================================================
     TASK-4850 — SAFE FAST PATH: delivery page "Next" -> Shopify Checkout
     ---------------------------------------------------------------------
     THE KILL SWITCH. ILS_FASTPATH_DEFAULT is the single toggle for everything
     this task added, in BOTH files that implement it. false restores the exact
     pre-TASK-4850 behaviour: schedulePersist() falls through to the TASK-3355
     queue, Next calls ensurePersisted() exactly as before, no listener below is
     registered and no readiness is ever computed. The resolved value is memoised
     on window.__ilsFastPath, which custom-gift-logic.js reads through the SAME
     resolver, so the two files can never end up half-enabled: whichever parses
     first decides and both then read one object. Two runtime overrides exist for
     testing and for support, and they win over the constant:
        window.ILS_FASTPATH_ENABLED = false     (set before the theme scripts run)
        ?ilsFast=off / ?ilsFast=on              (per-page-view)
     ---------------------------------------------------------------------
     WHAT IS IMPLEMENTED HERE (the selector half)

     A  BACKGROUND PRE-PERSIST WITHOUT AN EXPLICIT TAP. TASK-3355 only pre-wrote
        after `interacted`, which an auto-preselected courier country never sets.
        fpArm() now also opens the gate on the first user gesture of any kind, or
        FP_SETTLE_MS after load, whichever comes first — and on the pointerdown of
        Next itself, which precedes `click` by 70-150 ms on touch. `restoring`
        still suppresses everything, and the first pass starts from the cart body
        the page ALREADY read (loadCartWeight), so a refresh/bfcache replay whose
        cart is still correct costs exactly ZERO requests instead of a duplicate
        write.

     C  READINESS INVALIDATES ON CART LINE CHANGES. Readiness is keyed to BOTH
        the delivery selection signature AND a cart line signature
        (variant_id x quantity per line, in order, plus the line count — NOT the
        line key, which our own property write changes by design). It is only ever
        set from a cart body in which EVERY non-empty line was seen carrying all
        six expected _delivery_* values, so REPORT-4848 §7's unstamped-new-line gap
        cannot hide behind a matching attribute signature any more. Any observed
        cart mutation clears it SYNCHRONOUSLY, before the write even responds.

     D  OUR WRITES NO LONGER WAKE THE GIFT RECONCILER. Every cart write this file
        makes carries __ilsDeliveryMeta; custom-gift-logic.js's observer verifies
        the body really is attributes-only or _delivery_*-properties-only and then
        ignores it. Such a write cannot move the merchandise subtotal, so it can
        never change the gift decision. Genuine adds/removes/quantity/variant
        writes — including the delivery upsell's "+ Add" — are NOT marked and keep
        waking it exactly as before.

     E  FINAL-NEXT STATE MACHINE. See the click handler at the bottom.

     DELIBERATELY UNCHANGED: the write channels and their payload format
     (TASK-3157 attributes + TASK-3228 line properties), keepalive, and the SERIAL
     one-change.js-per-line pass — Shopify serialises cart mutations per token and
     REPORT-4848 §6 explicitly rejects parallelising them. The gift invariant stays
     wholly owned by custom-gift-logic.js; nothing here writes a gift line.
     ===================================================================== */
  var ILS_FASTPATH_DEFAULT = true;      /* <-- THE kill switch. false = legacy. */

  function resolveFastPath() {
    try {
      var w = window;
      if (w.__ilsFastPath && typeof w.__ilsFastPath.enabled === 'boolean') return w.__ilsFastPath;
      var on = ILS_FASTPATH_DEFAULT;
      if (w.ILS_FASTPATH_ENABLED === false) on = false;
      else if (w.ILS_FASTPATH_ENABLED === true) on = true;
      var qs = (w.location && w.location.search) || '';
      if (/[?&]ilsFast=off\b/.test(qs)) on = false;
      else if (/[?&]ilsFast=on\b/.test(qs)) on = true;
      w.__ilsFastPath = { enabled: !!on, owner: 'TASK-4850' };
      return w.__ilsFastPath;
    } catch (e) { return { enabled: false, owner: 'TASK-4850' }; }
  }
  var FASTPATH = resolveFastPath().enabled;

  /* =====================================================================
     TASK-4945 — ALWAYS-IMMEDIATE NEXT (stage 2 of REPORT-4935)
     ---------------------------------------------------------------------
     THE SECOND KILL SWITCH, separate from TASK-4850's on purpose.
     ILS_INSTANT_NEXT_DEFAULT gates ONLY the behaviour this task adds, so
     Production can be returned to the exact TASK-4854 blocking/readiness
     behaviour by flipping one token in each of the two files, with the
     TASK-4850 fast path left fully intact. Resolver, in precedence order:
        the constant  ->  window.ILS_INSTANT_NEXT_ENABLED  ->  ?ilsNext=on|off
     It is memoised on window.__ilsInstantNext, which custom-gift-logic.js reads
     through the IDENTICAL function, so the two files can never end up
     half-enabled however they are deployed.

     It is deliberately ANDed with FASTPATH: everything below depends on the
     TASK-4850 machinery (the cart-write observer, the arming gate, the
     background converge pass) to have written the canonical attributes in the
     first place. ?ilsFast=off therefore turns instant-next off too, which keeps
     "OFF restores TASK-4854 exactly" true for both switches.
     ---------------------------------------------------------------------
     WHY (REPORT-4935 sect. 1, measured). TASK-4854's fast path is a CACHE over a
     1 + N serialised write pass, and only the cache-HIT path was ever measured.
     Four mechanisms in the live code turn readiness false at exactly the moment a
     real shopper clicks: the gift validity token's 8-refresh-per-view budget
     (spent after ~2 minutes of dwell, then never re-armed), the 800 ms settle on
     an auto-preselected country, the 250 ms debounce after any selection tap, and
     the bounded yield to a busy gift reconciler. A cache miss put the whole
     1 + N pass back on the click. So the cache is not made better here — it is
     taken OFF the click path entirely.

     WHAT CHANGES
       * the canonical POST /cart/update.js {attributes: delivery_*} is issued
         IMMEDIATELY when a complete selection first exists or changes, with no
         settle and no debounce on the first one, and with NO read in front of it
         (update.js answers with the full cart, so the read was pure latency);
       * the N per-line _delivery_* change.js writes stay, purely as DERIVED
         background metadata — REPORT-4935 sect. 2.1 measured that the attribute
         channel alone reaches the order (25 of 25 sampled) and that
         swotzy_orders.js reads the UNION of the two channels, so nothing is lost
         if the lines lag;
       * the Next click is unconditionally  dedupe -> beacon -> assign. It awaits
         nothing, initiates no Cart API request, and never disables the button;
       * the durable bridge (TASK-4939, live in production) closes the one
         residual race: navigation can begin while the canonical write is still in
         flight, so the selection is ALSO sent to
         POST /api/delivery/selection via navigator.sendBeacon, at
         selection-complete AND again on Next. A post-order corrector fills any
         absent order attribute from that row, keyed on order.cart_token.
     ===================================================================== */
  var ILS_INSTANT_NEXT_DEFAULT = true;   /* <-- THE kill switch. false = TASK-4854. */

  function resolveInstantNext() {
    try {
      var w = window;
      if (w.__ilsInstantNext && typeof w.__ilsInstantNext.enabled === 'boolean') return w.__ilsInstantNext;
      var on = ILS_INSTANT_NEXT_DEFAULT;
      if (w.ILS_INSTANT_NEXT_ENABLED === false) on = false;
      else if (w.ILS_INSTANT_NEXT_ENABLED === true) on = true;
      var qs = (w.location && w.location.search) || '';
      if (/[?&]ilsNext=off\b/.test(qs)) on = false;
      else if (/[?&]ilsNext=on\b/.test(qs)) on = true;
      w.__ilsInstantNext = { enabled: !!on, owner: 'TASK-4945' };
      return w.__ilsInstantNext;
    } catch (e) { return { enabled: false, owner: 'TASK-4945' }; }
  }
  var INSTANT_NEXT = FASTPATH && resolveInstantNext().enabled;

  /* TASK-4945 / TASK-4939 — the durable bridge. Absolute URL: the storefront and
     the admin service are different origins, and the endpoint sits behind the
     existing strict-allowlist deliveryPublicCors. text/plain is the PRIMARY
     transport and that is a measured decision, not a preference: TASK-4916 found
     that a sendBeacon Blob of type application/json needs a CORS preflight and is
     SILENTLY DISCARDED by the browser while the page is navigating away —
     sendBeacon still returns true, so no fallback ever fires. The navigating
     instant is the only instant this bridge exists for. */
  var FP_BRIDGE_URL = 'https://alert-dedication-production.up.railway.app/api/delivery/selection';
  /* Exactly the seven fields the endpoint stores; every other key is dropped
     server-side before the write, so no PII can be stored even by accident. */
  var FP_BEACON_KEYS = [
    'delivery_country', 'delivery_type', 'delivery_carrier', 'delivery_carrier_name',
    'delivery_point_id', 'delivery_point_name', 'delivery_point_address'
  ];

  /* TASK-4945: the 800 ms settle is REPORT-4935 sect. 1.2 — on an auto-preselected
     courier country nothing arms the background persist for 800 ms because no
     gesture occurs, and a fast or cold click lands inside that window. Under
     instant-next the canonical write starts as early as the page can start it. */
  var FP_SETTLE_MS = INSTANT_NEXT ? 0 : 800;   /* A: auto-preselected selection settles, then writes */
  var FP_DEBOUNCE_MS = 250;      /* coalesce rapid selection changes into ONE final pass */
  var FP_REVALIDATE_MS = 450;    /* after an observed cart mutation, re-verify */
  var FP_READY_TTL_MS = 600000;  /* backstop only — every real invalidator is an event */
  var FP_MAX_ROUNDS = 4;         /* bound on the click-path convergence loop */
  var FP_GIFT_YIELD_MS = 300;    /* wait this long for a gift reconcile to finish */
  var FP_MAX_YIELDS = 12;        /* ...at most 12 times (~3.6 s), then go anyway */

  /* The six properties TASK-3228 stamps on every line. An expected value of ''
     and an ABSENT key are the same state (Shopify drops an empty property), so
     every comparison below normalises both sides to a string. */
  var FP_PROP_KEYS = [
    '_delivery_type', '_delivery_country', '_delivery_carrier',
    '_delivery_parcelshop_id', '_delivery_point_name', '_delivery_point_address'
  ];

  var fp = {
    armed: false,        /* A: background persistence allowed without a tap */
    armedBy: null,
    inFlight: null,      /* Promise of the running converge pass, or null */
    again: false,        /* a newer target appeared while a pass was running */
    timer: null,         /* debounce handle for the next pass */
    cartDirty: false,    /* a cart mutation we have not re-verified since */
    yields: 0,           /* consecutive deferrals waiting for the gift reconciler */
    lastError: null,     /* a REQUIRED write failed — readiness stays false */
    navigated: false,    /* the one-navigation latch */
    initCart: null,      /* the cart body loadCartWeight() already read */
    lastInvalidation: null,
    ready: { deliverySig: null, cartSig: null, at: 0, giftVersion: null, lines: 0, propsOk: 0 },
    /* TASK-4945 */
    attrsWrittenSig: null,  /* the signature the CANONICAL attributes write has landed for */
    cartToken: null,        /* BARE Shopify cart token, ?key= stripped (bridge join key) */
    beacon: { sent: 0, beaconOk: 0, fetchFallback: 0, noToken: 0, errors: 0, lastBody: null, lastWhy: null },
    stats: { passes: 0, reads: 0, writes: 0, stamped: 0, invalidations: 0, fastNavs: 0, slowNavs: 0, failures: 0, instantNavs: 0 }
  };

  /* Cart LINE signature. Deliberately key-independent: a line's Shopify key is
     variantId:propertiesHash, so our own property write CHANGES it and a
     key-based signature could never converge. variant_id x quantity per line, in
     cart order, plus the line count, moves on exactly the events that matter —
     add, remove, quantity +/-, variant swap, upsell add, gift add/remove. */
  function fpCartSig(cart) {
    var items = (cart && cart.items) || [];
    var parts = [];
    for (var i = 0; i < items.length; i++) {
      var it = items[i] || {};
      parts.push(String(it.variant_id) + 'x' + (it.quantity | 0));
    }
    return items.length + '|' + parts.join(',');
  }

  function fpLineNeedsStamp(item, props) {
    var p = (item && item.properties) || {};
    for (var i = 0; i < FP_PROP_KEYS.length; i++) {
      var k = FP_PROP_KEYS[i];
      var have = p[k] == null ? '' : String(p[k]);
      var want = props[k] == null ? '' : String(props[k]);
      if (have !== want) return true;
    }
    return false;
  }

  function fpAttrsMatch(cart, attrs) {
    var a = (cart && cart.attributes) || {};
    for (var k in attrs) {
      if (!Object.prototype.hasOwnProperty.call(attrs, k)) continue;
      var have = a[k] == null ? '' : String(a[k]);
      var want = attrs[k] == null ? '' : String(attrs[k]);
      if (have !== want) return false;
    }
    return true;
  }

  function fpClearReady() {
    fp.ready = { deliverySig: null, cartSig: null, at: 0, giftVersion: null, lines: 0, propsOk: 0 };
  }

  /* A cart mutation is in progress or has happened: readiness is gone NOW, not
     when the write responds. Also drops the cached init cart body. */
  function fpInvalidate(why) {
    if (!FASTPATH) return;
    fp.cartDirty = true;
    fp.initCart = null;
    fp.lastInvalidation = why || null;
    fp.stats.invalidations++;
    fpClearReady();
  }

  /* ---- the gift dimension, read-only ------------------------------------
     custom-gift-logic.js owns the gift invariant and its own checkout gate,
     which runs in the CAPTURE phase ahead of this file's handler. We only ever
     READ its proof, to decide whether this click can be a zero-request one:
     if its token is not armed the gate is going to do its blocking /cart.js
     anyway, so including it here costs nothing. An older build, a killed build
     (?ilsGift=off) or an unprovable state simply means "not a zero-request
     click" — never that the shopper is blocked, because fpEnsureReady() below
     converges the DELIVERY dimension only. */
  function fpGift() { try { return window.ILSGift || null; } catch (e) { return null; } }
  function fpGiftVersion() {
    var g = fpGift();
    try { return (g && typeof g.cartVersion === 'function') ? g.cartVersion() : null; } catch (e) { return null; }
  }
  function fpGiftReady() {
    var g = fpGift();
    if (!g || typeof g.fastReady !== 'function') return false;
    try { return g.fastReady() === true; } catch (e) { return false; }
  }
  /* Shopify serialises cart mutations per token, and REPORT-4848 §5 flagged
     exactly this window as a risk: our serial property pass interleaving with a
     gift update.js. Measured on Development under the first build of this task
     (verify.dev.on.json, D2): a re-stamp that raced a tier-crossing write left
     the gift write's effect invisible to the next read, so the reconciler wrote
     the identical atomic object a second time. The gift invariant is the more
     important writer and our stamping is never urgent, so the BACKGROUND
     scheduler yields to it — bounded, and never on the click path, where the
     gate has already finished its own pass before this file's handler runs. */
  function fpGiftBusy() {
    var g = fpGift();
    try { return !!(g && typeof g.isRunning === 'function' && g.isRunning()); } catch (e) { return false; }
  }

  /* The DELIVERY half of readiness: proven for this exact selection AND for the
     cart as we last verified it, with nothing pending and no failed write. */
  function fpDeliveryProven(sig) {
    if (!FASTPATH || !sig) return false;
    if (restoring) return false;
    if (fp.inFlight || fp.timer || fp.cartDirty || fp.lastError) return false;
    if (fp.ready.deliverySig !== sig) return false;
    if (!fp.ready.at || (Date.now() - fp.ready.at) > FP_READY_TTL_MS) return false;
    return true;
  }

  /* Full readiness — the ONLY state that allows a Next click with zero cart
     API requests. Delivery proven, gift proven, and both for the SAME cart. */
  function fpFastReady(sig) {
    if (!fpDeliveryProven(sig)) return false;
    if (!fpGiftReady()) return false;
    var gv = fpGiftVersion();
    if (gv === null || fp.ready.giftVersion !== gv) return false;
    return true;
  }

  /* ---- the writes. Identical payloads to TASK-3157/3228, plus the
     __ilsDeliveryMeta marker of CORE CHANGE D and keepalive. ---------------- */
  function fpGetCart() {
    fp.stats.reads++;
    return cartFetch('cart.js', { headers: { 'Accept': 'application/json' }, cache: 'no-store' })   /* TASK-5771 */
      .then(function (r) { if (!r.ok) throw new Error('cart.js HTTP ' + r.status); return r.json(); });
  }

  function fpPostAttributes(attrs) {
    fp.stats.writes++;
    return cartFetch('cart/update.js', {                       /* TASK-5771 route resolver */
      method: 'POST',
      keepalive: true,
      __ilsDeliveryMeta: true,
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ attributes: attrs })
    }).then(function (r) {
      if (!r.ok) throw new Error('cart update HTTP ' + r.status);
      return r.json();
    });
  }

  /* Stamp the six properties onto every line that does not already carry them,
     SERIALLY, by the line's key from the freshest snapshot we hold — the same
     contract as writeLineItemProperties(), narrowed to the lines that actually
     need a write (on a first pass that is all of them; after a cart mutation it
     is usually the one new line). Each response is a full cart, so the last one
     is the proof we verify against — no extra read. */
  function fpStampLines(cart, props) {
    var items = (cart && cart.items) || [];
    var todo = [];
    for (var i = 0; i < items.length; i++) {
      if ((items[i] && (items[i].quantity | 0) > 0) && fpLineNeedsStamp(items[i], props)) todo.push(items[i]);
    }
    if (!todo.length) return Promise.resolve(cart);
    var chain = Promise.resolve(cart);
    todo.forEach(function (it) {
      chain = chain.then(function () {
        fp.stats.writes++;
        fp.stats.stamped++;
        return cartFetch('cart/change.js', {                    /* TASK-5771 */
          method: 'POST',
          keepalive: true,
          __ilsDeliveryMeta: true,
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: it.key, quantity: it.quantity, properties: props })
        }).then(function (r) {
          if (!r.ok) throw new Error('change.js HTTP ' + r.status + ' for line ' + it.key);
          return r.json();
        });
      });
    });
    return chain;
  }

  /* ---- TASK-4945: the durable bridge ------------------------------------
     The cart token is the join key between a bridge row and the paid order
     (order.cart_token was present on 10 of 10 recent orders, REPORT-4935). It
     MUST be the BARE token: /cart.js returns "<token>?key=<hmac>", and only the
     part before ?key= matches order.cart_token and the endpoint's
     /^[A-Za-z0-9]{16,64}$/. Prefer a token from a cart body we already hold;
     fall back to the cart cookie, which is present from the first add. */
  function fpBareToken(t) {
    try { return String(t == null ? '' : t).split('?')[0].trim(); } catch (e) { return ''; }
  }
  function fpNoteCartToken(cart) {
    try {
      var t = fpBareToken(cart && cart.token);
      if (t) fp.cartToken = t;
    } catch (e) {}
    return cart;
  }
  function fpCartToken() {
    var t = fp.cartToken || '';
    if (!t) {
      try {
        var m = String(document.cookie || '').match(/(?:^|;\s*)cart=([^;]+)/);
        if (m) t = fpBareToken(decodeURIComponent(m[1]));
      } catch (e) {}
    }
    return /^[A-Za-z0-9]{16,64}$/.test(t) ? t : '';
  }

  /* Send the CURRENT complete selection to the durable bridge. Synchronous,
     fire-and-forget, and never awaited by anything — least of all by Next.
     Fired at BOTH selection-complete/change and the final Next, so losing the
     selection needs two independent failures. navigator.sendBeacon is the
     browser's own survives-unload channel and is NOT one of the Cart API
     endpoints this task must keep off the click path. The fallback is a
     keepalive fetch whose promise is dropped on the floor — text/plain is a
     CORS-safelisted content type, so it needs no preflight either. */
  function fpBeaconSelection(why) {
    if (!INSTANT_NEXT) return false;
    try {
      var sig = selectionSignature();
      if (!sig) return false;
      var attrs = JSON.parse(sig);
      var token = fpCartToken();
      if (!token) { fp.beacon.noToken++; return false; }
      var payload = { cart_token: token, source: 'dsel:' + String(why || ''), why: String(why || '') };
      for (var i = 0; i < FP_BEACON_KEYS.length; i++) {
        var k = FP_BEACON_KEYS[i];
        var v = attrs[k];
        if (v == null || v === '') continue;
        payload[k] = String(v);
      }
      var body = JSON.stringify(payload);
      /* Coalesce identical background beacons. The FINAL Next beacon is never
         suppressed: it is the one that must be on the wire as the document goes
         away, and last-write-wins on the bridge makes a repeat free. */
      if (why !== 'next' && fp.beacon.lastBody === body) return false;
      fp.beacon.lastBody = body;
      fp.beacon.lastWhy = String(why || '');
      fp.beacon.sent++;
      try {
        if (navigator && typeof navigator.sendBeacon === 'function' && typeof Blob === 'function') {
          if (navigator.sendBeacon(FP_BRIDGE_URL, new Blob([body], { type: 'text/plain' })) === true) {
            fp.beacon.beaconOk++;
            return true;
          }
        }
      } catch (e) {}
      try {
        fetch(FP_BRIDGE_URL, {
          method: 'POST', keepalive: true, mode: 'cors', cache: 'no-store',
          __ilsDeliveryMeta: true,
          headers: { 'Content-Type': 'text/plain' },
          body: body
        }).then(function () {}, function () {});
        fp.beacon.fetchFallback++;
        return true;
      } catch (e) {}
    } catch (e) {
      fp.beacon.errors++;
      try { console.warn('[dsel] bridge beacon failed:', e); } catch (e2) {}
    }
    return false;
  }

  /* ONE convergence pass: read (or reuse) the cart, write only what is actually
     missing, then PROVE the result from the body of the last response. Never
     rejects — a failure is recorded in fp.lastError and readiness stays false. */
  function fpConverge() {
    var sig = selectionSignature();
    if (!sig || restoring) { fpClearReady(); return Promise.resolve(); }
    var attrs;
    try { attrs = JSON.parse(sig); } catch (e) { fpClearReady(); return Promise.resolve(); }
    var props = lineItemPropsFromAttrs(attrs);
    fp.stats.passes++;
    fp.cartDirty = false;          /* THIS pass is the re-verification */

    /* The page already read /cart.js once (loadCartWeight, TASK-3210). Reusing
       that body makes the common "nothing actually changed" case — a refresh or
       a bfcache replay of an already-persisted selection — cost zero requests,
       which is what "must not cause unnecessary duplicate writes" requires. */
    var seed = fp.initCart;
    fp.initCart = null;

    /* TASK-4945 — THE CANONICAL WRITE, issued first and independently of the
       derived line pass. Three branches, in this order:

       (a) the body the page already read carries the right attributes already —
           a refresh or a bfcache replay of an already-persisted selection costs
           ZERO requests, which is what TASK-4850 required and this keeps;
       (b) instant-next, and the attributes have NOT landed for this signature:
           POST them NOW, with no read in front of the one write that matters.
           /cart/update.js answers with the full cart, so the line pass below
           loses nothing by skipping the read;
       (c) everything else — the legacy read-then-write-if-needed path, which is
           also the post-cart-mutation instant-next path: the attributes are
           already canonical for this signature, so only the DERIVED lines may
           need work and that needs a body. A background read, never a click. */
    var step;
    if (seed && fpAttrsMatch(seed, attrs)) {
      fp.attrsWrittenSig = sig;
      step = Promise.resolve(fpNoteCartToken(seed));
    } else if (INSTANT_NEXT && fp.attrsWrittenSig !== sig) {
      step = fpPostAttributes(attrs).then(function (cart) { fp.attrsWrittenSig = sig; return cart; });
    } else {
      step = (seed ? Promise.resolve(seed) : fpGetCart()).then(function (cart) {
        if (fpAttrsMatch(cart, attrs)) { fp.attrsWrittenSig = sig; return cart; }
        return fpPostAttributes(attrs).then(function (c) { fp.attrsWrittenSig = sig; return c; });
      });
    }

    return step.then(function (cart) {
      fpNoteCartToken(cart);
      return fpStampLines(cart, props);
    }).then(function (cart) {
      var items = (cart && cart.items) || [];
      var bad = 0;
      var ok = 0;
      for (var i = 0; i < items.length; i++) {
        if ((items[i] && (items[i].quantity | 0) <= 0)) continue;
        if (fpLineNeedsStamp(items[i], props)) bad++; else ok++;
      }
      if (!fpAttrsMatch(cart, attrs)) throw new Error('attributes not confirmed on the cart');
      if (bad) throw new Error(bad + ' line(s) still missing the delivery properties');
      fp.ready = {
        deliverySig: sig,
        cartSig: fpCartSig(cart),
        at: Date.now(),
        giftVersion: fpGiftVersion(),
        lines: items.length,
        propsOk: ok
      };
      fp.lastError = null;
      /* Keep the TASK-3355 queue's view consistent, so the legacy
         ensurePersisted() remains a correct no-op fallback. */
      writeState.writtenSig = sig;
      writeState.desiredSig = sig;
    }).catch(function (err) {
      fp.stats.failures++;
      fp.lastError = String((err && err.message) || err);
      /* TASK-4945 — the canonical attributes were NOT confirmed on the cart, so
         the latch must not suppress the re-write on the next pass. */
      fp.attrsWrittenSig = null;
      fpClearReady();
      try { console.error('[dsel] fast-path converge failed:', err); } catch (e2) {}
    });
  }

  /* Single flight, and convergence to the FINAL state only: a selection change
     or a cart mutation observed during a pass runs exactly ONE more pass
     afterwards rather than a pass per event. A pass that FAILED is not retried
     here — that retry belongs to the next user action or the Next click. */
  function fpRun() {
    if (fp.inFlight) { fp.again = true; return fp.inFlight; }
    fp.again = false;
    var p = fpConverge().then(function () {
      fp.inFlight = null;
      var want = selectionSignature();
      var more = fp.again || fp.cartDirty ||
                 (!fp.lastError && !!want && want !== fp.ready.deliverySig);
      fp.again = false;
      if (more) return fpRun();
    }, function () { fp.inFlight = null; });
    fp.inFlight = p;
    return p;
  }

  /* Debounced entry point. Called from syncSteps() (the single selection choke
     point), from the cart-mutation observers, and from the Next pointerdown. */
  function fpSchedule(delayMs) {
    if (!FASTPATH || restoring) return;
    var sig = selectionSignature();
    writeState.desiredSig = sig;
    if (!sig) { fpClearReady(); return; }
    /* TASK-4945 — the durable bridge, at selection-complete and on every change.
       Deliberately ABOVE the arm and proven guards: it issues no Cart API request
       at all, and the selection it carries is the one the shopper is about to
       check out with. Synchronous, and awaited by nothing. */
    if (INSTANT_NEXT) fpBeaconSelection('complete');
    if (!fp.armed && !interacted) return;      /* A: not before arm or a real tap */
    if (fpDeliveryProven(sig)) return;         /* already proven — nothing to do */
    /* TASK-4945 — the FIRST complete selection is NEVER debounced: the canonical
       write starts immediately (REPORT-4935 sect. 4.2.3). Only SUBSEQUENT changes
       coalesce, and even then the bridge already has the final state from the
       synchronous beacon above, so a debounced convergence loses nothing. */
    if (INSTANT_NEXT && fp.attrsWrittenSig === null && typeof delayMs !== 'number') delayMs = 0;
    if (fp.timer) clearTimeout(fp.timer);
    fp.yields = 0;
    fp.timer = setTimeout(function tick() {
      /* Yield to a gift reconciliation in flight (see fpGiftBusy). Readiness
         stays false while the timer is armed, so a click that arrives meanwhile
         takes the slow path, which clears this timer and forces the pass. */
      if (fpGiftBusy() && fp.yields < FP_MAX_YIELDS) {
        fp.yields++;
        fp.timer = setTimeout(tick, FP_GIFT_YIELD_MS);
        return;
      }
      fp.timer = null;
      fp.yields = 0;
      fpRun();
    }, typeof delayMs === 'number' ? delayMs : FP_DEBOUNCE_MS);
  }

  /* A — open the background-persist gate. Idempotent; the first caller wins and
     records why, which the harness asserts. */
  function fpArm(why) {
    if (!FASTPATH || fp.armed) return;
    fp.armed = true;
    fp.armedBy = why || 'unknown';
    fpSchedule(0);
  }

  /* CORE CHANGE E steps 4-6. Converge the DELIVERY dimension: wait for the pass
     that is already running, or start one, re-checking after each; resolve only
     on proof; never resolve behind a failed required write. Bounded, so a
     persistently failing backend can never hang the button. The gift dimension
     is NOT waited on here — custom-gift-logic.js's capture-phase gate has
     already run its own blocking reconciliation ahead of this handler, and
     making this stricter than that gate would strand shoppers it lets through. */
  function fpEnsureReady(sig) {
    if (!sig) return Promise.reject(new Error('incomplete selection'));
    return new Promise(function (resolve, reject) {
      var rounds = 0;
      (function loop() {
        if (fpDeliveryProven(sig)) { resolve(); return; }
        if (selectionSignature() !== sig) { reject(new Error('superseded')); return; }
        if (++rounds > FP_MAX_ROUNDS) {
          reject(new Error(fp.lastError || 'readiness not converging'));
          return;
        }
        if (fp.timer) { clearTimeout(fp.timer); fp.timer = null; }
        fp.lastError = null;                      /* this click IS the retry */
        var p = fp.inFlight || fpRun();           /* step 4 join / step 5 start */
        p.then(loop, loop);
      })();
    });
  }

  /* =====================================================================
     TASK-5771 (WORK-0128) — THE CHECKOUT HANDOFF.
     ---------------------------------------------------------------------
     THE DEFECT (measured live, REPORT-5770 section 2). The country the shopper
     confirms on this screen was written to cart attributes and line-item
     properties and NOWHERE ELSE. The hop into Shopify Checkout was one line,
     `window.location.href = localeRoot() + 'checkout'`, and localeRoot() is
     Shopify.routes.root — the market the shopper happened to be standing on
     when they entered the funnel. Shopify (non-Plus) derives the checkout's
     pre-selected shipping country from the MARKET, so a shopper who came from
     /ru-de/ and chose France reached a German checkout with Germany in
     Country/Region, and our own delivery-country-validation Function then
     BLOCKED completion on the mismatch. The chosen country was carried by
     nothing in that hop, and there is no supported link parameter that can
     carry it (REPORT-5770 section 4) — the market is the only lever.

     THE OWNER'S RULE (spec 2026-10-24, authoritative). The final selection on
     this screen is the ONE source of truth for the checkout transition. It is
     ALWAYS propagated. It is never compared against the previous storefront
     market to decide WHETHER to propagate, and there is no no-op when the two
     happen to agree: `force: true` below is that rule, in code. The storefront
     market the shopper arrived on is not an input to the decision, and the
     shipping country is not tied to the storefront language.

     HOW. One POST to Shopify's own /localization endpoint carrying the
     confirmed country, the locale the published matrix allows for it, and
     return_to=/checkout — so a single request moves the market AND lands the
     shopper in checkout in that market. It is the same engine, the same
     endpoint and the same field shape the footer card, the announcement strip
     and the homepage delivery card already commit with (TASK-5753/5764/5768),
     reached through ILSCountrySwitch.commitCountry(). Nothing here is a second
     country mechanism and nothing here holds a second country store.

     ORDERING. The cart must carry the selection BEFORE the market moves: the
     validation Function compares the checkout address against the
     `delivery_country` attribute and is a strict no-op when that attribute is
     absent, so arriving in checkout ahead of the write would mean arriving
     unenforced. navigateCheckout() therefore resolves the SAME persistence
     proof the legacy path used (fpEnsureReady / ensurePersisted) before it
     posts. In the common case that proof is already in hand and resolves on the
     spot, because the canonical write starts the instant the selection becomes
     complete (TASK-4945), long before the button is pressed.

     FAILURE. There is no silent fallback into the wrong market. If the commit
     cannot be made the shopper gets the localized retryable error and stays
     here, which is the owner's explicit instruction. The ONE exception is the
     case where plain navigation provably lands in the chosen country anyway
     (the market already IS that country): degrading there cannot produce a
     mismatch, and refusing to degrade would turn a config hiccup into a dead
     checkout button for every shopper. That equality test lives on the ERROR
     path only — it never decides whether to propagate, and it never runs
     before a commit has been attempted and has failed.

     ROLLBACK. `delivery_handoff` in snippets/custom-ils-cswitch.liquid, default
     false. With the flag off this whole block is unreachable and the funnel is
     byte-identical to TASK-4945: one `location.href`, no POST, no await.
     `?ilsHandoff=off` additionally disables it per page view. There is
     deliberately no `=on`: a query string must never be able to switch a
     feature ON for a shopper — the same rule as preview_ok in the stage island.
     ===================================================================== */
  var HANDOFF_WATCHDOG_MS = 9000;
  var nav = { posted: false, watchdog: 0, attempts: 0, lastError: null, lastPlan: null };

  function handoffOn() {
    try {
      var qs = (window.location && window.location.search) || '';
      if (/[?&]ilsHandoff=off\b/.test(qs)) return false;
      var cs = window.ILSCountrySwitch;
      if (!cs || typeof cs.flags !== 'function' || typeof cs.commitCountry !== 'function') return false;
      /* WORK-0074 interlock: if the retired second country control is ever
         switched on, this engine stands down rather than commit alongside it. */
      if (typeof cs.blocked_by_prior_art === 'function' && cs.blocked_by_prior_art()) return false;
      var f = cs.flags();
      return !!(f && f.delivery_handoff === true);
    } catch (e) { return false; }                  /* unreadable -> off */
  }

  /* The market Shopify reports for THIS request — server-rendered onto the
     section by TASK-5769, with Shopify.country as the fallback. Read ONLY to
     decide whether an already-failed commit can be safely degraded. */
  function serverCountry() {
    var c = root.getAttribute('data-shop-country') ||
            (window.Shopify && window.Shopify.country) || '';
    return String(c).trim().toUpperCase().slice(0, 2);
  }

  function handoffStatusRow() {
    var row = root.querySelector('[data-ils-handoff-status]');
    if (row) return row;
    row = document.createElement('div');
    row.className = 'ils-dsel__status ils-dsel__status--handoff';
    row.setAttribute('data-ils-handoff-status', '');
    row.setAttribute('role', 'alert');
    row.appendChild(document.createTextNode(I18N.error + ' '));
    /* The retry re-enters the SAME click handler, so a retry is a whole fresh
       attempt: persistence is re-proven and exactly one new POST is made. */
    row.appendChild(retryBtn(function () {
      clearHandoffError();
      if (elNext) elNext.click();
    }));
    var footer = root.querySelector('.ils-dsel__footer');
    var inner = footer && footer.querySelector('.ils-dsel__footer-row');
    if (footer && inner) footer.insertBefore(row, inner);
    else if (footer) footer.appendChild(row);
    else root.appendChild(row);
    return row;
  }
  function clearHandoffError() {
    var row = root.querySelector('[data-ils-handoff-status]');
    if (row && row.parentNode) row.parentNode.removeChild(row);
  }

  function clearHandoffWatchdog() {
    if (nav.watchdog) { clearTimeout(nav.watchdog); nav.watchdog = 0; }
  }

  /* Give the button back and say so. Never strands the shopper on a dead
     control, and never navigates. */
  function handoffFail(reason) {
    nav.lastError = reason;
    nav.posted = false;
    fp.navigated = false;
    nextBusy = false;
    clearHandoffWatchdog();
    if (elNext) {
      elNext.removeAttribute('aria-busy');
      elNext.classList.remove('is-loading');
    }
    try { handoffStatusRow(); } catch (e) {}
    try { console.error('[dsel] checkout handoff failed:', reason); } catch (e) {}
    try {
      document.dispatchEvent(new CustomEvent('ils:dsel-handoff', {
        detail: { ok: false, reason: reason, attempts: nav.attempts }
      }));
    } catch (e) {}
  }

  /* TASK-5865 — THE TAP HAS TO SAY SOMETHING. Visual only: see the build
     script's header for the measurement this closes. nextBusy and fp.navigated
     are still the only latches; this neither awaits nor gates anything. */
  function markHandoffBusy() {
    if (!elNext) return;
    try {
      elNext.setAttribute('aria-busy', 'true');
      elNext.classList.add('is-loading');
    } catch (e) {}
  }

  /* A real form POST hands the 302 to the browser, which is exactly why it is
     used (the cookie Shopify sets belongs to the next document). The cost is
     that a non-302 answer is Shopify's own page, not a value we can read. What
     IS observable is that we are still here: if the navigation has not started
     well after it should have, the commit did not take — and since the POST
     writes nothing to the cart, a retry is safe. */
  function armHandoffWatchdog() {
    clearHandoffWatchdog();
    nav.watchdog = setTimeout(function () {
      nav.watchdog = 0;
      handoffFail('no_navigation');
    }, HANDOFF_WATCHDOG_MS);
  }

  function handoffCommit(want) {
    var cs = window.ILSCountrySwitch;
    if (!cs || typeof cs.commitCountry !== 'function') { handoffDegrade(want, 'engine_absent'); return; }
    var r;
    try {
      /* force: the owner's ALWAYS-propagate rule. return_to: the market move
         and the arrival in checkout are one request, so there is no moment in
         which the market and the destination disagree. */
      r = cs.commitCountry(want, { force: true, return_to: '/checkout' });
    } catch (e) {
      handoffDegrade(want, 'threw:' + ((e && e.message) || e));
      return;
    }
    nav.lastPlan = r || null;
    /* An in-flight POST from this same document (a double tap that beat our own
       latch) IS the one POST this attempt wanted. */
    if ((r && r.ok) || (r && r.reason === 'already_committing')) {
      try {
        document.dispatchEvent(new CustomEvent('ils:dsel-handoff', {
          detail: {
            ok: true, country: want, attempts: nav.attempts,
            locale: (r.plan && r.plan.locale) || null,
            locale_fallback: !!(r.plan && r.plan.locale_fallback),
            return_to: r.return_to || null,
            reused: r.reason === 'already_committing'
          }
        }));
      } catch (e2) {}
      armHandoffWatchdog();
      return;
    }
    handoffDegrade(want, 'commit:' + ((r && r.reason) || 'unknown'));
  }

  /* ERROR PATH ONLY. See the header: this runs after a commit attempt has
     already failed, and it is not the propagation decision. Plain navigation is
     allowed only when it provably lands in the country the shopper confirmed. */
  function handoffDegrade(want, reason) {
    if (want && want === serverCountry()) {
      try { console.warn('[dsel] handoff unavailable (' + reason + ') - market already ' + want + ', navigating plainly'); } catch (e) {}
      window.location.href = localeRoot() + 'checkout';
      return;
    }
    handoffFail(reason);
  }

  /* THE ONE checkout navigation. The fast path, the instant path and the legacy
     path reach checkout through here and nowhere else. */
  function navigateCheckout() {
    if (nav.posted) return;                        /* one POST per attempt */
    nav.posted = true;
    nav.attempts++;
    if (!handoffOn()) { window.location.href = localeRoot() + 'checkout'; return; }
    var want = String(state.country || '').toUpperCase();
    if (!want) { handoffFail('no_country'); return; }
    var sig = selectionSignature();
    /* TASK-5865 — SAY IT NOW, UNCONDITIONALLY, for every tap that reaches this
       point. Measured on live Production, real WebKit, /en-lt/: no branch below
       is instant any more. BEST case (proof already in hand, 3-line cart) still
       spends 1.5-2.5 s on the /localization POST plus a full document
       navigation that TASK-5771 added AFTER the proof; WORST case measured
       (14-line cart) spends 9 935 ms on the awaited convergence before the POST
       is even issued. A condition on fpDeliveryProven() would therefore leave
       the COMMON case unmarked, which is the state this task had to close.
       Visual only — nextBusy and fp.navigated are still the only latches — and
       deliberately placed AFTER the handoffOn() early return so the rollback
       path (flag off: one location.href, no POST, no await) is untouched. */
    markHandoffBusy();
    /* Persist first, always. Resolves immediately when the write is already
       proven, which is the common case. */
    (FASTPATH ? fpEnsureReady(sig) : ensurePersisted(sig)).then(
      function () { handoffCommit(want); },
      function (err) { handoffDegrade(want, 'persist:' + ((err && err.message) || err)); }
    );
  }

  /* Exactly one navigation, however many clicks or replays arrive. */
  function fpNavigateCheckout() {
    if (fp.navigated) return;
    fp.navigated = true;
    navigateCheckout();
  }

  /* ---- C: every signal that the cart's lines may have moved ---------------
     A passive pass-through fetch observer, the same shape custom-add-toast.js
     and custom-gift-logic.js already use: it reads the URL, passes the call
     through untouched and returns the CALLER'S OWN promise — the response is
     never consumed, cloned or delayed. Our own marked writes are skipped; every
     other cart write, INCLUDING custom-gift-logic.js's own gift writes (which a
     gift add/remove makes a line change for us), invalidates readiness. This is
     installed independently of the gift file on purpose: with ?ilsGift=off there
     is no gift observer at all, and a stale readiness there is exactly the 0/6
     unstamped-line bug this task must close. */
  var FP_CART_WRITE_RE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  function fpInstallCartObserver() {
    var native = window.fetch;
    if (typeof native !== 'function') return;
    window.fetch = function (input, init) {
      var watch = false;
      try {
        if (!(init && init.__ilsDeliveryMeta) && !(input && input.__ilsDeliveryMeta)) {
          var u = '';
          if (typeof input === 'string') u = input;
          else if (input && typeof input.url === 'string') u = input.url;
          watch = !!u && FP_CART_WRITE_RE.test(u);
        }
      } catch (e) {}
      var p = native.apply(this, arguments);
      if (watch) {
        fpInvalidate('cart-write');
        try {
          p.then(function () { fpSchedule(FP_REVALIDATE_MS); },
                 function () { fpSchedule(FP_REVALIDATE_MS); });
        } catch (e) {}
      }
      return p;
    };
  }

  function fpInit() {
    if (!FASTPATH) return;
    fpInstallCartObserver();

    /* Dawn's pubsub + the drawer's / cart page's DOM event, when present. */
    try {
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
        subscribe(PUB_SUB_EVENTS.cartUpdate, function (ev) {
          fpInvalidate('pubsub-cartUpdate');
          fpSchedule(FP_REVALIDATE_MS);
        });
      }
    } catch (e) {}
    document.addEventListener('cart:update', function () {
      fpInvalidate('dom-cart-update');
      fpSchedule(FP_REVALIDATE_MS);
    });
    /* custom-gift-logic.js morphed the cart: a gift line may have appeared and
       it needs the six properties like any other line. */
    document.addEventListener('ils:gift-reconciled', function () {
      fpInvalidate('gift-reconciled');
      fpSchedule(FP_REVALIDATE_MS);
    });
    /* Returning to this tab is the one moment an out-of-document cart change
       becomes visible to us. Re-verify — a READ first, so an unchanged cart
       costs no writes at all. */
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState !== 'visible') return;
      fpInvalidate('visible');
      fpSchedule(FP_REVALIDATE_MS);
    });
    /* A window blur -> focus invalidator for the side-by-side-WINDOW case was
       built and REMOVED here for the same measured reason as in
       custom-gift-logic.js: window focus is raised by ordinary in-page focus
       moves too, and it invalidated a valid readiness snapshot on the Next click
       itself. visibilitychange above is the covered, specified case. */

    /* A — the first user gesture of any kind opens the persist gate, and so does
       a short settle delay, whichever comes first. fpArm() is idempotent. */
    var onGesture = function () { fpArm('gesture'); };
    ['pointerdown', 'keydown', 'touchstart', 'scroll'].forEach(function (t) {
      try { document.addEventListener(t, onGesture, { passive: true, capture: true }); }
      catch (e) { try { document.addEventListener(t, onGesture, true); } catch (e2) {} }
    });
    setTimeout(function () { fpArm('settle'); }, FP_SETTLE_MS);

    /* A — pointerdown on Next precedes its click by 70-150 ms on touch: boost an
       unready state there rather than pay for it on the click path. */
    if (elNext) {
      try {
        elNext.addEventListener('pointerdown', function () {
          fpArm('next-pointerdown');
          /* TASK-4945 — under instant-next NOTHING may be initiated because of
             this gesture. The click that follows it 70-150 ms later navigates in
             single-digit milliseconds, so a cart request started here could only
             ever be an abandoned one, and "no cart API initiated because of this
             click" has to mean the whole gesture, not just the click event.
             fpArm() above is already a no-op by this point: with instant-next the
             settle arm fires at load, not 800 ms in. */
          if (INSTANT_NEXT) return;
          if (!fpDeliveryProven(selectionSignature())) fpSchedule(0);
        }, { passive: true });
      } catch (e) {}
    }

    /* Read-mostly surface for the verification harness and for support. */
    window.ILSDsel = {
      version: 'TASK-4850',
      instantNextVersion: 'TASK-4945',
      fastPath: function () { return !!FASTPATH; },
      instantNext: function () { return !!INSTANT_NEXT; },
      bridgeUrl: function () { return FP_BRIDGE_URL; },
      cartToken: function () { return fpCartToken(); },
      beacon: function () { return Object.assign({}, fp.beacon); },
      ready: function () { return fpFastReady(selectionSignature()); },
      deliveryProven: function () { return fpDeliveryProven(selectionSignature()); },
      snapshot: function () {
        return {
          sig: selectionSignature(), interacted: interacted, restoring: restoring,
          armed: fp.armed, armedBy: fp.armedBy, cartDirty: fp.cartDirty,
          inFlight: !!fp.inFlight, timer: !!fp.timer, yields: fp.yields, lastError: fp.lastError,
          lastInvalidation: fp.lastInvalidation, navigated: fp.navigated,
          ready: fp.ready, stats: fp.stats,
          giftReady: fpGiftReady(), giftVersion: fpGiftVersion(),
          writtenSig: writeState.writtenSig,
          instantNext: !!INSTANT_NEXT, attrsWrittenSig: fp.attrsWrittenSig,
          cartToken: fpCartToken(), beacon: Object.assign({}, fp.beacon)
        };
      },
      invalidate: function (why) { fpInvalidate(why || 'manual'); },
      converge: function () { return fpRun(); },
      arm: function (why) { fpArm(why || 'manual'); }
    };
  }

  /* ---- INIT (TASK-3163) --------------------------------------------------
     Load the country/method matrix once, build the dropdown from the countries
     the API actually serves, pick the default country from the storefront
     locale, then hand off to selectCountry() (which loads that country's rates
     and builds the methods). */
  /* TASK-5769 — when may a persisted selection outrank the store country?
     TASK-3220 persists the whole in-progress selection in sessionStorage so an F5
     or a bfcache "back" does not throw the shopper’s progress away, and TASK-4945
     persists even a fully AUTO-preselected selection (manual:false) so Next →
     checkout is instant. Together those made the saved country beat the server on
     every later entry into this step, which is the defect the owner reported on
     2026-10-24: add to cart on /ru-de/ → delivery step preselects Germany and
     persists it → change the store country to France in the header → re-enter the
     delivery step → it still said "Германия", with the German courier rate,
     while the header, strip, footer and Shopify.country all said France.

     The rule: a saved selection is only usable while the STORE COUNTRY it was
     made under is still the store country. saveState() stamps `shop`.
       - same store country  -> keep it. A deliberate in-step pick (manual) and an
         in-progress method/operator/point therefore still survive reload, back
         and bfcache exactly as before — this step does not preempt the shopper.
       - store country moved -> drop it, including a manual pick: switching the
         market in the header IS the shopper stating a newer destination, and the
         saved method/operator/pickup point belong to the old country anyway.
       - legacy blob written before this fix (no `shop` stamp) -> only usable if it
         was an explicit pick or already agrees with the computed default, so a
         stale auto-default from an open tab cannot survive this deploy. */
  function savedIsUsable(saved, def) {
    if (!saved) return false;
    var iso = String(saved.country || '').toLowerCase();
    if (!iso) return false;
    var stamp = String(saved.shop || '').toLowerCase();
    if (stamp) return !!SHOP_COUNTRY && stamp === SHOP_COUNTRY;
    return !!saved.manual || iso === def;
  }

  /* TASK-4833 — preselection ladder. First served country wins:
       (a) an EXPLICIT earlier choice — handled one level up in
           startInitialSelection() from the persisted state (saved.manual), so it
           outranks every signal computed here for as long as the store country it
           was made under is still the store country (TASK-5769, savedIsUsable);
           when the shopper moves the market in the header, that newer statement
           of destination wins and the saved pick is dropped, not recomputed around;
       (b) TASK-5769 — the applied Shopify Market country (SHOP_COUNTRY, from the
           section's data-shop-country = localization.country.iso_code), which the
           owner's 2026-10-24 cross-surface invariant makes the source of truth for
           the country preselected when the shopper arrives from the cart;
       (c) the logged-in customer's default-address country (CUSTOMER_COUNTRY,
           emitted by the section from the storefront `customer` object) — this was
           TASK-4833's (b), demoted below the market by TASK-5769;
       (d) a native IP/geolocation suggestion — NOT wired: this storefront ships
           no Shopify geolocation surface (no browsing_context_suggestions, no
           localization country form on this page) and the task forbids adding a
           third-party geo service, so there is nothing to read here;
       (e) the market default — the country half of the locale route, then
           FALLBACK_ORDER, then simply the first served country;
       (f) manual selection stays available at every point — the dropdown is
           never disabled and a tap sets manualCountry (see renderCountryList).
     No GPS / navigator.geolocation prompt, and navigator.language is NOT used
     as a country detector. */
  function resolveDefaultCountry(available) {
    var avail = {};
    available.forEach(function (c) { avail[c] = true; });
    var cand = [];
    function push(v) { if (v) cand.push(String(v).trim().toLowerCase()); }

    // (b) TASK-5769 — the applied Shopify Market country, server-rendered.
    // Promoted ABOVE the customer address by the owner's 2026-10-24 cross-surface
    // invariant: the store country the shopper is actually shopping in wins. A
    // logged-in shopper with a saved PL address browsing /ru-de/ is shopping in
    // Germany and must see Germany preselected here, exactly like the header says.
    push(SHOP_COUNTRY);

    // (c) the logged-in customer's default/shipping address country — TASK-4833's
    // step (b), demoted to a fallback for when (b) yields nothing servable.
    push(CUSTOMER_COUNTRY);

    // (e) market default from the locale route ('/en-lv/' → lv, '/de/' → de).
    try {
      var r = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '';
      var seg = r.replace(/^\/+|\/+$/g, '').split('/')[0];   // 'en-lv' | 'de' | ''
      if (seg) {
        var parts = seg.split('-');
        push(parts[parts.length - 1]);     // country half of en-lv
        push(parts[0]);                    // bare /de
      }
    } catch (e) {}

    for (var i = 0; i < cand.length; i++) { if (avail[cand[i]]) return cand[i]; }
    for (var j = 0; j < FALLBACK_ORDER.length; j++) { if (avail[FALLBACK_ORDER[j]]) return FALLBACK_ORDER[j]; }
    return available[0] || null;
  }

  // TASK-3349: build the country dropdown groups from a given availability list
  // (lowercased ISO codes). The source is normally the API config, but at init we
  // seed from the server-rendered country names so the list is populated instantly
  // — see seedCountryList(). Priority markets first (fixed order), rest A→Z at render.
  function buildCountryGroups(avail) {
    if (!avail) avail = Object.keys((CONFIG && CONFIG.countries) || {}).map(function (c) { return c.toLowerCase(); });
    var set = {};
    avail.forEach(function (c) { set[String(c).toLowerCase()] = true; });
    // TASK-4833: no grouping any more — one flat list of served ISO codes. The
    // display order is decided at render time by the locale collator.
    AVAILABLE = Object.keys(set);
  }

  // TASK-3349: the ISO codes we have a (translated) name for — the fixed 27 EU
  // markets emitted by Liquid in data-country-names. This is the source of truth
  // for WHICH countries the dropdown lists; the live config only refines it.
  function countryNameKeys() {
    return Object.keys(COUNTRY_NAMES).map(function (c) { return c.toLowerCase(); });
  }

  // TASK-3349: populate the dropdown IMMEDIATELY from the server-rendered country
  // names, so all 27 countries are visible the instant the page renders — identically
  // on every locale. Previously the list was built ONLY inside loadConfig(), after
  // /api/delivery/config resolved; and that call was chained behind /cart.js and hit
  // a cold-startable Railway backend. On any locale where that fetch was slow (cold
  // start) or failed, opening the dropdown showed an EMPTY list while the country
  // button sat on the localized "Loading…" text (e.g. lt-lt "Įkeliama…") — the
  // regression this task fixes (the warm default locale en-eu tended to load in time,
  // so it looked like "only en-eu has the list"). The API config still reconciles the
  // list afterwards (loadConfig), and the per-country rates drive methods as before.
  function seedCountryList() {
    var names = countryNameKeys();
    if (!names.length) return false;
    buildCountryGroups(names);
    // Show the default country name right away so the button is never blank; the
    // initial selection (below) confirms/overrides it once the cart weight is read.
    // TASK-4833: an explicitly chosen country owns the button label from the
    // first paint. Without this the button briefly showed the ladder's answer
    // and then swapped to the shopper's own pick once startInitialSelection ran
    // — visually an overwrite of a manual choice.
    var saved = loadSavedState();
    var savedIso = saved && String(saved.country || '').toLowerCase();
    var seedDef = resolveDefaultCountry(AVAILABLE);
    // TASK-5769: the same usability gate startInitialSelection() applies, so the
    // FIRST PAINT never shows a country the market has since moved away from.
    var label = (saved && saved.manual && savedIso && AVAILABLE.indexOf(savedIso) !== -1 && savedIsUsable(saved, seedDef))
      ? savedIso
      : seedDef;
    if (label) elCountryName.textContent = countryName(label);
    renderCountryList('');
    return true;
  }

  // TASK-3349: choose + load the initial country (a restored selection or the
  // locale default) against the currently-seeded list. Split out of loadConfig so
  // the visible country list no longer waits on the config fetch. Kept behind the
  // cart-weight read (loadCartWeight) so the first courier price is weight-accurate
  // (TASK-3210). Availability is name-based here (config may still be in flight).
  function startInitialSelection() {
    var avail = AVAILABLE;
    var def = resolveDefaultCountry(avail);
    if (!def) { elCountryName.textContent = countryName('de'); showMethodsError(loadConfig); return; }
    var saved = loadSavedState();
    // TASK-5769: a saved selection no longer outranks the store country by the
    // mere fact of existing — see savedIsUsable().
    var savedOk = saved && avail.indexOf(String(saved.country || '').toLowerCase()) !== -1
                  && savedIsUsable(saved, def);
    if (saved && !savedOk) {
      // The market moved under this blob. Clear it so the old country’s method /
      // operator / pickup point cannot be replayed, and so the cart attributes the
      // Next button writes are rebuilt for the country actually being shipped to.
      try { sessionStorage.removeItem(STATE_KEY); } catch (e) {}
      manualCountry = false;
    }
    if (savedOk) {
      // TASK-4833: step (a) — carry the "this was an explicit choice" bit back into
      // this page view so the pick stays sticky across further reloads instead of
      // decaying into a recomputed default.
      if (saved.manual) manualCountry = true;
      restoreSelection(saved).catch(function () { selectCountry(def); });
    } else {
      selectCountry(def);
    }
  }

  // TASK-3220: replay a selection saved in sessionStorage (see saveState()).
  // Chains country → method → operator → point, awaiting each async load so the
  // downstream step only fires once its data is ready. `restoring` suppresses
  // autoscroll + re-saving during the replay; a final saveState() re-persists the
  // reconstructed state (incl. the point object). Any gap (country/operator no
  // longer served) stops the replay cleanly at the last valid step.
  function restoreSelection(saved) {
    restoring = true;
    // TASK-4850: the replay's own selectMethod/selectPoint calls all ran while
    // `restoring` was true, so syncSteps() suppressed persistence and nothing
    // calls it again once the replay ends. Re-enter the scheduler here (a no-op
    // when the fast path is OFF, where a restore must stay write-free until the
    // shopper taps) so a RESTORED complete selection is verified too — starting
    // from a read, so an already-correct cart costs zero writes.
    var finish = function () {
      restoring = false;
      saveState();
      if (FASTPATH) fpSchedule(0);
    };
    return selectCountry(saved.country).then(function () {
      if (state.country !== saved.country) return finish();
      if (saved.method === 'courier') { selectMethod('courier'); return finish(); }
      if (saved.method !== 'locker') return finish();
      selectMethod('locker');
      if (!saved.operator || !operatorAvailable(saved.operator)) return finish();
      return selectOperator(saved.operator).then(function () {
        if (state.operator === saved.operator && saved.point) {
          // Ensure the saved point exists in state.points so collapsePoint() can
          // render the summary even if it wasn't on the first fetched page.
          if (!pointById(saved.point) && saved.pointObj) state.points.push(saved.pointObj);
          if (pointById(saved.point)) selectPoint(saved.point);
        }
        finish();
      });
    }).catch(function () { restoring = false; });
  }

  // TASK-3349: config is now a RECONCILER, not the list builder. It runs in
  // PARALLEL with the seeded list + initial selection (loadConfig no longer gates
  // whether the dropdown has any countries). When it arrives we narrow the list to
  // the countries the backend actually serves ∩ the names we can label; if it fails
  // or returns nothing served we KEEP the full seeded 27 (fail-open — far better to
  // show every EU country than an empty list). It never blanks the list or the
  // in-progress selection; a genuine API outage still surfaces in the method area
  // via the per-country /rates call.
  function loadConfig() {
    return apiGet('/api/delivery/config').then(function (data) {
      CONFIG = (data && data.countries) ? data : { countries: {} };
      var nameSet = {};
      countryNameKeys().forEach(function (c) { nameSet[c] = true; });
      var served = Object.keys(CONFIG.countries)
        .map(function (c) { return c.toLowerCase(); })
        .filter(function (c) { return nameSet[c]; });
      // Fail-open: only narrow when the backend actually returned labelled countries.
      buildCountryGroups(served.length ? served : countryNameKeys());
      // Re-render if the dropdown is open right now so it reflects the reconciled set.
      if (elCountryPop && !elCountryPop.hidden) {
        renderCountryList(elCountrySearch ? elCountrySearch.value : '');
      }
    }).catch(function () {
      // Config unavailable → keep the full seeded list. (No blank list, no dead end.)
    });
  }

  // TASK-3157: on Next (only enabled once the path is complete) write the choice
  // to cart attributes, then redirect to checkout. Guarded against double-taps.
  var nextBusy = false;
  elNext.addEventListener('click', function (e) {
    e.preventDefault();
    if (elNext.disabled || nextBusy) return;
    // TASK-3340: never proceed to checkout with an empty cart (e.g. someone opened
    // /pages/delivery-selector directly). Only a CONFIRMED empty count blocks —
    // if the /cart.js read failed (cartItemCount still null) we fail open and let
    // Shopify's own empty-cart handling take over. Bounce them back to the cart.
    if (cartItemCount === 0) { window.location.href = localeRoot() + 'cart'; return; }

    /* =================================================================
       TASK-4945 — THE ALWAYS-IMMEDIATE PATH. One unconditional branch, no
       readiness snapshot, no cache to miss:

          dedupe repeated taps  ->  bridge beacon  ->  location.assign(checkout)

       It awaits nothing and INITIATES no Cart API request: not fpEnsureReady(),
       not ensurePersisted(), not a gift reconcile, not /cart.js, /cart/update.js,
       /cart/change.js or /cart/add.js, and not a validation read. Whatever the
       background has or has not finished is irrelevant to the navigation, which
       is the whole point — readiness was a cache, and REPORT-4935 sect. 1 measured
       that a real shopper MISSES it.

       Correctness does not rest on the background finishing either. The canonical
       attributes write was started the instant the selection became complete, and
       the beacon below puts the SAME final selection into the durable bridge
       (TASK-4939) through the browser's own survives-unload channel, from where a
       post-order corrector fills any attribute the order reached Shopify without.
       Bridge writes are last-write-wins, so the beacon fired here — after every
       selection-complete beacon — is by definition the state the shopper checked
       out with.

       The button is NEVER disabled, frozen or marked busy as a synchronisation
       mechanism: nextBusy is set purely as the one-tap latch (fp.navigated is the
       second), and pageshow clears it on a bfcache return exactly as before. */
    if (INSTANT_NEXT) {
      if (fp.navigated) return;
      nextBusy = true;
      fp.stats.instantNavs++;
      /* Never awaited, never blocking; a throw inside is swallowed there. */
      fpBeaconSelection('next');
      try {
        document.dispatchEvent(new CustomEvent('ils:dsel-instant-next', {
          detail: {
            deliverySig: selectionSignature(),
            attrsWrittenSig: fp.attrsWrittenSig,
            beacon: Object.assign({}, fp.beacon),
            proven: fpDeliveryProven(selectionSignature()),
            lines: (fp.ready && fp.ready.lines) || 0
          }
        }));
      } catch (e4) {}
      fpNavigateCheckout();
      return;
    }

    /* TASK-4850 CORE CHANGE E steps 1-3. Repeated clicks collapse on `nextBusy`,
       which is set before anything async. Then ONE readiness snapshot: when the
       current selection AND the current cart lines are both proven — every line
       carrying the six _delivery_* values, attributes confirmed, nothing in
       flight, no failed write, and custom-gift-logic.js's own validity token
       armed for this same cart version — there is nothing left to do, so we
       perform ZERO cart API requests and navigate immediately. This is the whole
       point of the task: REPORT-4848's 1 879 ms (3 lines) / 3 138 ms (5 lines)
       were 1 + N serialised Cart API round trips that had all already been paid
       for in the background by the time the shopper pressed the button. */
    if (FASTPATH) {
      var fpSig = selectionSignature();
      if (fpFastReady(fpSig)) {
        nextBusy = true;
        fp.stats.fastNavs++;
        try {
          document.dispatchEvent(new CustomEvent('ils:dsel-fastpath', {
            detail: {
              ready: true, deliverySig: fpSig, cartSig: fp.ready.cartSig,
              lines: fp.ready.lines, propsOk: fp.ready.propsOk,
              giftVersion: fp.ready.giftVersion, provenAt: fp.ready.at
            }
          }));
        } catch (e3) {}
        fpNavigateCheckout();
        return;
      }
    }

    nextBusy = true;
    elNext.setAttribute('aria-busy', 'true');
    elNext.classList.add('is-loading');

    // TASK-3355: the selection has usually already been persisted to the cart in
    // the background (attributes via /cart/update.js + line-item properties via
    // /cart/change.js — see the incremental persist queue above). ensurePersisted()
    // resolves IMMEDIATELY when the CURRENT selection is already confirmed on the
    // cart (the common case → the redirect is effectively instant), otherwise it
    // awaits (and if needed kicks) the write so we NEVER redirect to checkout with
    // stale or incomplete delivery attributes (the P0 risk from TASK-3267). Both
    // channels are still written exactly as before (TASK-3157 attributes + TASK-3228
    // line-item properties, TASK-3353 single-cart-fetch saving); this only moves
    // WHEN they run off the Next → checkout critical path. keepalive on the writes
    // lets any final leg finish across the navigation.
    // TASK-4850 CORE CHANGE E steps 4-6 — with the fast path ON, converge the
    // delivery dimension: join the pass already in flight, or start one, re-check
    // readiness, and NEVER navigate behind a failed required write. With the kill
    // switch OFF this is the untouched TASK-3355 call.
    (FASTPATH ? fpEnsureReady(selectionSignature()) : ensurePersisted(selectionSignature()))
      .then(function () {
        fp.stats.slowNavs++;
        /* TASK-5771 — both paths now reach checkout through the SAME
           navigateCheckout(). fpNavigateCheckout() is kept as the fast path's
           own one-navigation latch and delegates to it, so there is exactly one
           place in this file that can start a checkout navigation. */
        if (FASTPATH) { fpNavigateCheckout(); return; }
        navigateCheckout();
      })
      .catch(function (err) {
        // Re-enable so the shopper can retry; never strand them on a dead button.
        nextBusy = false;
        elNext.removeAttribute('aria-busy');
        elNext.classList.remove('is-loading');
        try { console.error('[dsel] delivery persist failed:', err); } catch (e2) {}
      });
  });

  // TASK-3220: when the shopper reaches checkout then taps the browser BACK
  // button, the page is usually served from the back/forward cache (bfcache) with
  // the whole JS state frozen exactly as it was left — including `nextBusy = true`
  // and the button's is-loading / aria-busy state from the outgoing redirect. That
  // left the Next button inert (every click hit the `nextBusy` guard) until a full
  // refresh. `pageshow` fires on both a fresh load AND a bfcache restore, so we
  // clear the busy latch here and Next works again immediately, no refresh needed.
  window.addEventListener('pageshow', function (e) {
    nextBusy = false;
    /* TASK-5771 — the handoff latches are per-attempt, not per-document. A BACK
       out of checkout (or out of a commit the shopper abandoned) must leave the
       button fully usable, and the watchdog armed for the outgoing POST must not
       fire into a page the shopper has come BACK to and report a failure that
       did not happen. */
    nav.posted = false;
    clearHandoffWatchdog();
    clearHandoffError();
    if (elNext) {
      elNext.removeAttribute('aria-busy');
      elNext.classList.remove('is-loading');
    }
    // TASK-4850: a bfcache restore comes back with the whole JS state frozen —
    // including the one-navigation latch — and the cart may have been changed in
    // the checkout the shopper just backed out of, or in another tab. Drop the
    // latch and force a fresh verification. It starts with a READ, so an
    // unchanged cart costs zero writes however many times this fires.
    if (FASTPATH) {
      fp.navigated = false;
      if (e && e.persisted) { fpInvalidate('pageshow-persisted'); fpSchedule(FP_REVALIDATE_MS); }
    }
  });

  // TASK-3347: the footer "Back" LEAVES the widget back to the shop cart — where
  // the Checkout redirect brings the shopper from (TASK-3340/3345) — instead of
  // stepping through the internal country→method→operator→point stack (TASK-3137's
  // behaviour, which confused shoppers who expect "Back" to return to where they
  // came from, not undo one sub-selection). Explicit /cart redirect (not
  // history.back()) is predictable even when the page was opened via a direct link
  // or with an empty history; localeRoot() keeps the shopper in their market+locale
  // (the same helper the forward checkout / empty-cart-bounce navigations use).
  if (elBack) {
    elBack.addEventListener('click', function (e) {
      e.preventDefault();
      window.location.href = localeRoot() + 'cart';
    });
  }
  // TASK-3139: the header back arrow was removed (logo-only topbar).

  // TASK-3210: read the actual cart weight once (grams → kg) so the courier
  // price for the 23 weight-priced EU countries reflects the real order. Fetched
  // locale-aware (same market/locale as the cart write). Always resolves — a
  // failure/empty cart leaves cartWeightKg null (→ lightest tier, a safe floor).
  function loadCartWeight() {
    return cartFetch('cart.js', { headers: { 'Accept': 'application/json' } })   /* TASK-5771 */
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (c) {
        if (c && typeof c.total_weight === 'number') cartWeightKg = c.total_weight / 1000;
        if (c && typeof c.item_count === 'number') cartItemCount = c.item_count; // TASK-3340
        if (c && typeof c.total_price === 'number') {
          /* TASK-5795 — total_price is PRESENTMENT minor units and the
             threshold it is compared against is EUR, so on /en-se/ a 150,00 kr
             cart read as "EUR 150" and this screen — the last one before
             Checkout — printed the courier as free. Converted with Shopify's
             own rate; null means we do not know, and isFree() below then
             refuses rather than guesses. */
          cartSubtotalEur = (window.ILSMoney && window.ILSMoney.on())
            ? window.ILSMoney.eurFromCents(c.total_price)
            : c.total_price / 100; // TASK-3539
        }
        // TASK-4850: keep this body — the first readiness pass verifies against
        // it instead of issuing a second /cart.js, so an already-persisted
        // selection (refresh, bfcache replay) costs ZERO extra requests.
        if (FASTPATH && c && Array.isArray(c.items)) fp.initCart = c;
        /* TASK-4945 — learn the BARE cart token from the read the page already
           does, so the bridge beacon never has to fall back to the cookie. */
        if (INSTANT_NEXT && c) fpNoteCartToken(c);
      })
      .catch(function () { /* keep cartWeightKg = null */ });
  }

  // Kick everything off (TASK-3349):
  //  1. Seed the FULL country list synchronously from the server-rendered names —
  //     so the dropdown is populated instantly and identically on every locale, with
  //     zero dependency on the (cold-startable) delivery API's timing or health.
  //  2. Reconcile the list against the live config IN PARALLEL (non-blocking; narrows
  //     to backend-served countries, fail-open to the full list on any error).
  //  3. Read the cart weight, then pick the initial country (restored or default) so
  //     the first courier price is still weight-accurate (TASK-3210).
  if (!seedCountryList()) elCountryName.textContent = I18N.loading;  // names missing → old behaviour
  showMethodsLoading();
  fpInit();   // TASK-4850 — no-op when the kill switch is OFF
  loadConfig();
  loadCartWeight().then(startInitialSelection);
})();
